/**
 * UTILITIES LIBRARY
 *
 * @description General global JavaScript utility functions.
 * @namespace utils
 */

((theme) => {
  /**
   * Class of general JavaScript utilities.
   */
  class Utilities {
    constructor() {
      // Initialize functions extending objects
      this._handleize();

      // Keep track of all unique numbers generated
      this._rendered_numbers = [];
    }

    /**
     * Initializes the handleize function on the String object.
     * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
     *
     * @private
     * @memberof Utilities
     */
    _handleize() {
      // UTF to ASCII map. Removes accents.
      var defaultDiacriticsRemovalap = [
        {
          base: "A",
          letters:
            "\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F",
        },
        { base: "AA", letters: "\uA732" },
        { base: "AE", letters: "\u00C6\u01FC\u01E2" },
        { base: "AO", letters: "\uA734" },
        { base: "AU", letters: "\uA736" },
        { base: "AV", letters: "\uA738\uA73A" },
        { base: "AY", letters: "\uA73C" },
        {
          base: "B",
          letters: "\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181",
        },
        {
          base: "C",
          letters:
            "\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E",
        },
        {
          base: "D",
          letters:
            "\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779",
        },
        { base: "DZ", letters: "\u01F1\u01C4" },
        { base: "Dz", letters: "\u01F2\u01C5" },
        {
          base: "E",
          letters:
            "\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E",
        },
        { base: "F", letters: "\u0046\u24BB\uFF26\u1E1E\u0191\uA77B" },
        {
          base: "G",
          letters:
            "\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E",
        },
        {
          base: "H",
          letters:
            "\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D",
        },
        {
          base: "I",
          letters:
            "\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197",
        },
        { base: "J", letters: "\u004A\u24BF\uFF2A\u0134\u0248" },
        {
          base: "K",
          letters:
            "\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2",
        },
        {
          base: "L",
          letters:
            "\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780",
        },
        { base: "LJ", letters: "\u01C7" },
        { base: "Lj", letters: "\u01C8" },
        {
          base: "M",
          letters: "\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C",
        },
        {
          base: "N",
          letters:
            "\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4",
        },
        { base: "NJ", letters: "\u01CA" },
        { base: "Nj", letters: "\u01CB" },
        {
          base: "O",
          letters:
            "\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C",
        },
        { base: "OI", letters: "\u01A2" },
        { base: "OO", letters: "\uA74E" },
        { base: "OU", letters: "\u0222" },
        { base: "OE", letters: "\u008C\u0152" },
        { base: "oe", letters: "\u009C\u0153" },
        {
          base: "P",
          letters:
            "\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754",
        },
        { base: "Q", letters: "\u0051\u24C6\uFF31\uA756\uA758\u024A" },
        {
          base: "R",
          letters:
            "\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782",
        },
        {
          base: "S",
          letters:
            "\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784",
        },
        {
          base: "T",
          letters:
            "\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786",
        },
        { base: "TZ", letters: "\uA728" },
        {
          base: "U",
          letters:
            "\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244",
        },
        {
          base: "V",
          letters: "\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245",
        },
        { base: "VY", letters: "\uA760" },
        {
          base: "W",
          letters:
            "\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72",
        },
        { base: "X", letters: "\u0058\u24CD\uFF38\u1E8A\u1E8C" },
        {
          base: "Y",
          letters:
            "\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE",
        },
        {
          base: "Z",
          letters:
            "\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762",
        },
        {
          base: "a",
          letters:
            "\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250",
        },
        { base: "aa", letters: "\uA733" },
        { base: "ae", letters: "\u00E6\u01FD\u01E3" },
        { base: "ao", letters: "\uA735" },
        { base: "au", letters: "\uA737" },
        { base: "av", letters: "\uA739\uA73B" },
        { base: "ay", letters: "\uA73D" },
        {
          base: "b",
          letters: "\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253",
        },
        {
          base: "c",
          letters:
            "\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184",
        },
        {
          base: "d",
          letters:
            "\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A",
        },
        { base: "dz", letters: "\u01F3\u01C6" },
        {
          base: "e",
          letters:
            "\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD",
        },
        { base: "f", letters: "\u0066\u24D5\uFF46\u1E1F\u0192\uA77C" },
        {
          base: "g",
          letters:
            "\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F",
        },
        {
          base: "h",
          letters:
            "\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265",
        },
        { base: "hv", letters: "\u0195" },
        {
          base: "i",
          letters:
            "\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131",
        },
        { base: "j", letters: "\u006A\u24D9\uFF4A\u0135\u01F0\u0249" },
        {
          base: "k",
          letters:
            "\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3",
        },
        {
          base: "l",
          letters:
            "\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747",
        },
        { base: "lj", letters: "\u01C9" },
        {
          base: "m",
          letters: "\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F",
        },
        {
          base: "n",
          letters:
            "\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5",
        },
        { base: "nj", letters: "\u01CC" },
        {
          base: "o",
          letters:
            "\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275",
        },
        { base: "oi", letters: "\u01A3" },
        { base: "ou", letters: "\u0223" },
        { base: "oo", letters: "\uA74F" },
        {
          base: "p",
          letters:
            "\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755",
        },
        { base: "q", letters: "\u0071\u24E0\uFF51\u024B\uA757\uA759" },
        {
          base: "r",
          letters:
            "\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783",
        },
        {
          base: "s",
          letters:
            "\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B",
        },
        {
          base: "t",
          letters:
            "\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787",
        },
        { base: "tz", letters: "\uA729" },
        {
          base: "u",
          letters:
            "\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289",
        },
        {
          base: "v",
          letters: "\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C",
        },
        { base: "vy", letters: "\uA761" },
        {
          base: "w",
          letters:
            "\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73",
        },
        { base: "x", letters: "\u0078\u24E7\uFF58\u1E8B\u1E8D" },
        {
          base: "y",
          letters:
            "\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF",
        },
        {
          base: "z",
          letters:
            "\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763",
        },
      ];

      // Generate the diacritics map to replace accented characters.
      var diacriticsMap = {};
      for (var i = 0; i < defaultDiacriticsRemovalap.length; i++) {
        var letters = defaultDiacriticsRemovalap[i].letters.split("");

        for (var j = 0; j < letters.length; j++) {
          diacriticsMap[letters[j]] = defaultDiacriticsRemovalap[i].base;
        }
      }

      /**
       * Replaces accented characters with their non-accented counterparts.<br>
       * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
       *
       * @param {boolean} [remove=false] - Whether to remove unicode characters that are not mapped to a non-accented characters.
       * 	This includes all non-ascii characters.
       * @param {string} [replacement=Empty string] - The replacement character(s) to use for invalid characters.
       * @returns {string} String without accents.
       * @memberof Utilities
       * @example
       * // returns 'My accented string$.'
       * 'My áccentéd string$.'.noAccents();
       */
      String.prototype.noAccents = function (remove, replacement) {
        // x00-x7E is the ASCII character range
        var withoutAccents = this.replace(/[^\u0000-\u007E]/g, function (ch) {
          return diacriticsMap[ch] || ch;
        });

        replacement = replacement || "";

        if (remove) {
          withoutAccents = withoutAccents.replace(
            /[^\u0000-\u007E]/g,
            replacement
          );
        }

        return withoutAccents;
      };

      /**
       * Returns a "handleized" version of a string suitable for URLs and file names.<br>
       * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
       *
       * @param {string} [seperator=-] - The character to use as a separator.
       * @param {string} [replacement=Empty string] - The replacement character(s) to use for invalid characters.
       * @returns {string} The handleized string.
       * @memberof Utilities
       * @example
       * var myString = 'My áccentéd string$.';
       *
       * // returns 'my-accented-string'
       * myString.handleize();
       *
       * // returns 'my_accented_string##'
       * myString.handleize('_', '#');
       */
      String.prototype.handleize = function (separator, replacement) {
        var slug;
        separator = separator || "-";
        replacement = replacement || "";
        slug = this.noAccents(true, replacement)
          .trim()
          .replace(/[^a-z0-9\s-]/gi, replacement)
          .replace(/\s/gi, separator);

        return slug.toLowerCase();
      };
    }

    /**
     * Curency formating function.
     *
     * @param {(number|string)} price - The price to format.
     * @param {object} options - The options for the currency formatter.
     * @param {boolean} [options.cents=false] - Wether or not the price is currently in cents.
     * @param {string} [options.locale=Shop locale or 'en-CA'] - Override the locale to format to.
     * @param {string} [options.currency=Shop currency or 'CAD'] - Override the currency to output.
     * @param {boolean} [options.no_zeros=false] - To output the price without decimal zeros.
     * @returns {(string|null)} The price formatted based on the locale and currency.
     * @memberof Utilities
     * @example
     * // Returns '$12.56'
     * theme.utils.toMoney(12.56)
     *
     * // Returns '12,56 $'
     * theme.utils.toMoney('12.56', { locale: 'fr-CA', currency: 'CAD' })
     *
     * // Returns '$12.56'
     * theme.utils.toMoney('1256', { cents: true })
     */
    toMoney(price, options = {}) {
      if (!price) {
        console.error("Utilities.toMoney: Invalid price passed.", price);
        return null;
      }

      var price = new Number(price);
      var currency_options = {
        style: "currency",
      };

      // Assign defaults if no options are passed.
      options.cents = options.cents || false;
      options.locale =
        options.locale || (theme.shop ? theme.shop.locale : null) || "en-CA";
      currency_options.currency =
        options.currency || (theme.shop ? theme.shop.currency : null) || "CAD";

      // Convert to dollars if the amount passed is in cents
      if (options.cents) {
        price = price / 100;
      }

      // Check if there are decimals and no zero option
      if (options.no_zeros && !price.toString().split(".")[1]) {
        currency_options.minimumSignificantDigits = 2;
      }

      return price.toLocaleString(options.locale, currency_options);
    }

    /**
     * Removes the URL protocol.
     *
     * @param {string} path - The URL to strip out the protocol from.
     * @returns {(string|null)} The URL without protocol.
     * @memberof Utilities
     */
    removeProtocol(path) {
      if (!path) {
        console.error("Utilities.removeProtocol: Invalid path passed.", path);
        return null;
      }

      return path.replace(/http(s)?:/, "");
    }

    /**
     * Adds a Shopify size attribute to an image URL.
     *
     * @param {string} src - The image source URL.
     * @param {string} size - The size parameters to fetch, following Shopify's img_url guidelines.
     * @returns {(string|null)} The updated URL.
     * @memberof Utilities
     * @example
     * theme.utils.getSizedImageUrl('cdn.shopify.com/some-image.jpg', '500x300_crop_center');
     */
    getSizedImageUrl(src, size) {
      if (!size) {
        console.error("Utilities.getSizedImageUrl: Invalid size passed.", size);
        return src;
      }

      if (size === "master") {
        return this.removeProtocol(src);
      }

      var match = src.match(
        /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i
      );

      if (match) {
        var prefix = src.split(match[0]);
        var suffix = match[0];

        return this.removeProtocol(prefix[0] + "_" + size + suffix);
      } else {
        console.error(
          "Utilities.getSizedImageUrl: Invalid image URL passed.",
          src
        );
        return null;
      }
    }

    /**
     * Created an object from a parameter string.
     *
     * @param {string} object_string - The string to parse to an object.
     * @returns {object} The object created from the string.
     * @memberof Utilities
     * @example
     * // If called on a form that takes a name and email, could return { name: "Joe", email: "joe@diffagency.com" }
     * theme.utils.toObject($form.serialize());
     */
    toObject(object_string) {
      var parameters = {};
      var elements;

      if (!object_string) {
        return {};
      }

      // Split into each element
      elements = object_string.split("&");

      elements.forEach((element) => {
        let equals_index = element.indexOf("=");
        // check if the query has a value or is only a key
        if (equals_index == -1) {
          // if there's no = then it's only a key, so set the key to the whole element and the value to null
          // Decode to get original characters
          parameters[decodeURIComponent(element)] = null;
        } else {
          // if there's an = then assign up to the = to be the key and everything after to be the value (or null if empty)
          // Decode to get original characters
          let key = decodeURIComponent(element.slice(0, equals_index));
          let value =
            decodeURIComponent(element.slice(equals_index + 1)) || null;
          parameters[key] = value;
        }
      });

      return parameters;
    }

    /**
     * Extracts the URL parameters into an object.
     *
     * @param {string} [url=window.location.href] - The URL to parse.
     * @returns {object} The query parameters object.
     * @memberof Utilities
     * @example
     * // Returns { name: "Joe", email: "joe@diffagency.com" }
     * theme.utils.getURLParams('https://example.com/?name%3DJoe%26email%3Djoe%40diffagency.com');
     */
    getURLParams(url) {
      var url = url || window.location.href;
      // Get the string after the '?' from the URL
      var query_string = url.split(/\?([^\#]+)/)[1];

      if (!query_string) {
        return {};
      }

      return this.toObject(query_string);
    }

    /**
     * Sets URL parameters.
     *
     * @param {object} [params] - The parameters to add to the URL.
     * @param {object} [options] - Method options.
     * @param {boolean} [options.clear=false] - Clear previous parameters.
     * @param {boolean} [options.no_overwrite=false] - Don't overwrite parameters with same key.
     * @param {boolean} [options.push_state=false] - Push a window history state. Defaults to replace state.
     * @memberof Utilities
     * @example
     * // Examples with a current URL of 'example.com/?page=2'
     *
     * // Will set url to 'example.com/?page=3&view=grid'
     * theme.utils.setURLParams({ page: 3, view: 'grid' });
     *
     * // Will set url to 'example.com/?page=2'
     * theme.utils.setURLParams({ page: 3 }, { no_overwrite: true });
     *
     * // Will clear parameters: 'example.com/'
     * theme.utils.setURLParams({}, { clear: true });
     *
     * // Will replace parameters: 'example.com/?view=grid'
     * theme.utils.setURLParams({ view: 'grid' }, { clear: true });
     */
    setURLParams(params, options = {}) {
      const current_params = this.getURLParams();
      let new_params = options.clear ? {} : current_params;
      let query_array = [];
      let query_string = "";

      // Add new parameters
      Object.keys(params).forEach((key) => {
        let param_value = params[key];
        new_params[key] = options.no_overwrite
          ? new_params[key] || param_value
          : param_value;
      });

      // Convert object to URL string
      Object.keys(new_params).forEach((key) => {
        query_array.push(
          encodeURIComponent(key) + "=" + encodeURIComponent(new_params[key])
        );
      });

      if (query_array.length) {
        query_string = "?" + query_array.join("&");
      }

      if (options.push_state) {
        window.history.pushState({}, null, query_string);
      } else {
        window.history.replaceState({}, null, query_string);
      }
    }

    /**
     * Returns a modified version of the passed object based on the passed parameters.
     *
     * @param {object} object - The object to modify.
     * @param {object} parameters - The parameters to update.
     * @param {object} [options] - Function options.
     * @param {boolean} [options.no_overwrite=false] - Option to not overwrite existing parameters
     * @param {boolean} [options.clear=false] - To empty the object before updating it.
     * @memberof Utilities
     * @example
     * // Returns { name: 'Joe', age: 18, height: 2 }
     * theme.utils.modifyObject(
     * 	{ name: 'Joe', age: 17 },
     * 	{ age: 18, height: 2 }
     * );
     *
     * // Returns { name: 'Joe', age: 17, height: 2 }
     * theme.utils.modifyObject(
     * 	{ name: 'Joe', age: 17 },
     * 	{ age: 18, height: 2 },
     * 	{ no_overwrite: true }
     * );
     *
     * // Returns { age: 18, height: 2 }
     * theme.utils.modifyObject(
     * 	{ name: 'Joe', age: 17 },
     * 	{ age: 18, height: 2 },
     * 	{ clear: true }
     * );
     */
    modifyObject(object, parameters, options) {
      options = options || {};
      let new_object = options.clear ? {} : object || {};

      if (!object || !parameters || typeof parameters !== "object") {
        return new_object || {};
      }

      Object.keys(parameters).forEach((key) => {
        new_object[key] = options.no_overwrite
          ? new_object[key] || parameters[key]
          : parameters[key];
      });

      return new_object;
    }

    /**
     * Generates a unique number.
     *
     * @param {number} [length=6] - The length of the unique number
     * @returns Unique number.
     * @memberof Utilities
     * @example
     *
     * // Could generate 66145
     * theme.utils.uniqueNumber(5);
     */
    uniqueNumber(length) {
      var length = length || 6;
      var id = Math.floor(Math.random() * Math.pow(10, length));

      while (this._rendered_numbers.indexOf(id) !== -1) {
        id = Math.floor(Math.random() * Math.pow(10, length));
      }

      // Keep track of the generated number so that it is never generated again.
      this._rendered_numbers.push(id);

      return id;
    }
  }

  // Global export
  theme.utils = new Utilities();
})((window.theme = window.theme || {}));

/**
 * CART LIBRARY
 *
 * @description Handles the Shopify cart interactions.
 * @namespace cart
 */

((theme, $) => {
  /**
   * Class representing the cart.
   */
  class Cart {
    /**
     * Creates an instance of Cart.
     *
     * @param {object} [cart] - A cart object to use. Passing this argument will avoid an AJAX call to the Shopify API.
     * @constructs Cart
     * @memberof Cart
     */
    constructor(cart) {
      this.content = cart || {};
      this._queue = $.when();

      // If no cart is passed, fetch it.
      if (!cart) {
        this.fetch();
      }
    }

    // ------------------------------
    //  Getters and Setters
    // ------------------------------

    /**
     * The cart object.<br><br>
     * @example
     * // Get the cart object
     * theme.cart.content
     *
     * @type {object}
     * @fires Cart:set
     * @memberof Cart
     */
    get content() {
      return this._object;
    }

    set content(cart) {
      // The Cart class uses a private variable to keep track of the cart content.
      this._object = cart || this._object;

      /**
       * The cart has been updated.
       * The event is attached to the document element.<br><br>
       *
       * @event Cart:set
       * @property {object} cart - The cart object.
       * @memberof Cart
       * @example
       * $(document).on('Cart:set', function(event, cart) {});
       */
      $(document).trigger("Cart:set", this.content);
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Queries the Shopify API to refresh the cart.<br>
     * Calling this function will update the cart object.
     *
     * @param {object} [options] - Properties to change the behaviour of fetch()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:fetch
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * theme.cart.fetch();
     */
    fetch({ silent = false } = {}) {
      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart.js",
          type: "GET",
          dataType: "json",
        })
          .done((cart) => {
            this.content = cart;

            if (!silent) {
              /**
               * The cart has been fetched.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:fetch
               * @property {object} cart - The cart object.
               * @memberof Cart
               * @example
               * $(document).on('Cart:fetch', function(event, cart) {});
               */
              $(document).trigger("Cart:fetch", this.content);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error("Cart.fetch: Failed getting the cart.", resp);
          });
      });

      return this._queue;
    }

    /**
     * Adds an item to cart.
     *
     * @param {(object|number)} item - The item id or item object to add.
     * @param {number} item.id - The id of the variant to add.
     * @param {number} [item.quantity=1] - Quantity to add, defaults to 1.
     * @param {object} [options] - Properties to augment the behaviour of add()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:add
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Adds 1 of item 1234567 to the cart.
     * theme.cart.add(1234567);
     */
    add(item, { silent = false } = {}) {
      if (!item) {
        console.warn("Cart.add: No item was passed.");
        return $.when();
      }

      // Set up the object if only an id is passed.
      if (typeof item != "object") {
        item = {
          id: item,
        };
      }

      // Default the quantity to 1 if none is passed.
      item.quantity = item.quantity || 1;

      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart/add.js",
          data: item,
          type: "POST",
          dataType: "json",
        })
          .done((item) => {
            // Refresh the cart
            this.fetch({ silent: silent });

            if (!silent) {
              /**
               * An item has been added to cart.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:add
               * @property {object} item - The item added to cart.
               * @memberof Cart
               * @example
               * $(document).on('Cart:add', function(event, item) {});
               */

              $(document).trigger("Cart:add", item);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error("Cart.add: Failed adding to the cart.", resp);
          });
      });

      return this._queue;
    }

    /**
     * Updates an item in the cart.
     *
     * @param {(object|object[]|number|number[])} items - The item id, id array, item object or object array to update.
     * @param {number} items.id - The id of the variant to update.
     * @param {number} [items.quantity=1] - Quantity to add, defaults to 1.
     * @param {object} [options] - Properties to augment the behaviour of update()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:update
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Updated item 1234567's quantity to 5
     * theme.cart.update({ id: 1234567, quantity: 5 });
     *
     * // Updated item 1234567's quantity to 5 and item 7654321's quantity to 1
     * theme.cart.update([{ id: 1234567, quantity: 5 }, 7654321]);
     */
    update(items, { silent = false } = {}) {
      if (!items) {
        console.warn("Cart.update: No item was passed.");
        return $.when();
      }

      let data = {};

      /**
       * Internal function to convert items to the proper format for Shopify's update API call.
       *
       * @param {object} data - The data object to add the key/value pair to.
       * @param {(object|number)} item - The item id or item object to format.
       * @private
       */
      const _addUpdateItem = (item) => {
        // Set up the object if only an id is passed.
        if (typeof item === "object") {
          if (!item.id) {
            console.warn(
              "Cart.update: No id was passed with the product object."
            );
            return $.when();
          }
          data[item.id] =
            item.quantity || item.quantity === 0 ? item.quantity : 1;
        } else {
          data[item] = 1;
        }
      };

      if (Array.isArray(items)) {
        items.forEach((item) => {
          _addUpdateItem(item);
        });
      } else {
        _addUpdateItem(items);
      }

      // If data is empty, return empty promise
      if (!Object.keys(data).length) {
        return $.when();
      }

      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart/update.js",
          data: { updates: data },
          type: "POST",
          dataType: "json",
        })
          .done((cart) => {
            // Refresh the cart
            this.content = cart;

            if (!silent) {
              /**
               * An item in the cart has been updated.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:update
               * @property {object} cart - The cart object.
               * @property {object} item - The item changed.
               * @memberof Cart
               * @example
               * $(document).on('Cart:update', function(event, cart, item) {});
               */
              $(document).trigger("Cart:update", [cart, items]);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error("Cart.update: Failed updating a cart item.", resp);
          });
      });

      return this._queue;
    }

    /**
     * Change an item in the cart.
     *
     * @param {(object|number)} item - The item id or item object to update.
     * @param {number} item.id - The id of the variant to update.
     * @param {number} [item.quantity=1] - Quantity to add, defaults to 1.
     * @param {object} [options] - Properties to augment the behaviour of change()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:change
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Changes item 1234567's quantity to 2
     * theme.cart.change({ id: 1234567, quantity: 2 });
     */
    change(item, { silent = false } = {}) {
      if (!item) {
        console.warn("Cart.change: No item was passed.");
        return $.when();
      }

      // Set up the object if only an id is passed.
      if (typeof item != "object") {
        item = {
          id: item,
          quantity: 1,
        };
      }

      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart/change.js",
          data: item,
          type: "POST",
          dataType: "json",
        })
          .done((cart) => {
            // Refresh the cart
            this.content = cart;

            if (!silent) {
              /**
               * An item in the cart's quantity has been changed.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:change
               * @property {object} cart - The cart object.
               * @property {object} item - The item changed.
               * @memberof Cart
               * @example
               * $(document).on('Cart:change', function(event, cart, item) {});
               */
              $(document).trigger("Cart:change", [cart, item]);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error(
              "Cart.change: Failed changing a cart quantity.",
              resp
            );
          });
      });
      return this._queue;
    }

    /**
     * Updates an item in the cart.
     *
     * @param {(object|object[]|number|number[])} item - The item id, id array, item object or object array to remove.
     * @param {object} [options] - Properties to augment the behaviour of remove()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Removes item 1234567 from the cart
     * theme.cart.remove(1234567);
     */
    remove(items, { silent = false } = {}) {
      if (!items) {
        console.warn("Cart.remove: No item was passed.");
        return $.when();
      }

      // Create an array of items with quantity 0 and feed to the update function.
      let remove_items = [];

      if (Array.isArray(items)) {
        items.forEach((item) => {
          remove_items.push({
            id: typeof item === "object" ? item.id : item,
            quantity: 0,
          });
        });
      } else {
        remove_items.push({
          id: typeof items === "object" ? items.id : items,
          quantity: 0,
        });
      }

      return this.update(remove_items, { silent: silent });
    }

    /**
     * Empty the cart.
     *
     * @param {object} [options] - Properties to augment the behaviour of clear()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:clear
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * theme.cart.clear();
     */
    clear({ silent = false } = {}) {
      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart/clear.js",
          type: "POST",
          dataType: "json",
        })
          .done((cart) => {
            // Refresh the cart
            this.content = cart;

            if (!silent) {
              /**
               * The cart has been cleared.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:clear
               * @property {object} cart - The cart object.
               * @memberof Cart
               * @example
               * $(document).on('Cart:clear', function(event, cart) {});
               */
              $(document).trigger("Cart:clear", cart);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error("Cart.clear: Failed clearing the cart.", resp);
          });
      });

      return this._queue;
    }

    /**
     * Updates an attribute in the cart.
     *
     * @param {object} attributes - The attribute(s) to update.
     * @param {object} [options] - The options to pass
     * @param {boolean} [options.no_overwrite=false] - Option to skip setting attributes if they are already set.
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @fires Cart:attributeUpdate
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Update the language attribute to 'fr' and currency to CAD
     * theme.cart.updateAttributes({ langauge: 'fr', currency: 'CAD' });
     *
     * // Update the language attribute to 'fr' but don't overwrite if already set
     * theme.cart.updateAttributes({ langauge: 'fr' }, { no_overwrite: true });
     */
    updateAttributes(
      attributes,
      { no_overwrite = false, silent = false } = {}
    ) {
      if (!attributes) {
        console.warn("Cart.updateAttribute: No attributes were passed.");
        return $.when();
      }

      let data = {};

      // If we don't overwrite, only add attributes that are not set in cart
      if (no_overwrite) {
        for (let key of Object.keys(attributes)) {
          if (!this.content.attributes[key]) {
            data[key] = attributes[key];
          }
        }
      } else {
        data = attributes;
      }

      // If data is empty, return empty promise
      if (!Object.keys(data).length) {
        return $.when();
      }

      this._queue = this._queue.then(() => {
        return $.ajax({
          url: "/cart/update.js",
          data: { attributes: data },
          type: "POST",
          dataType: "json",
        })
          .done((cart) => {
            // Refresh the cart
            this.content = cart;

            if (!silent) {
              /**
               * An item in the cart has been updated.
               * The event is attached to the document element.<br><br>
               *
               * @event Cart:attributeUpdate
               * @property {object} cart - The cart object.
               * @property {object} attributes - The attributes changed.
               * @memberof Cart
               * @example
               * $(document).on('Cart:attributeUpdate', function(event, cart, attributes) {});
               */
              $(document).trigger("Cart:update", [cart, data]);
            }
          })
          .fail((resp) => {
            this._queue = $.when();
            console.error(
              "Cart.attributeUpdate: Failed updating a cart attribute.",
              resp
            );
          });
      });

      return this._queue;
    }

    /**
     * Clears all or specific attributes in the cart.
     *
     * @param {(string|string[])} [attributes] - The attribute(s) to clear. If none are passed, all will be cleared.
     * @param {object} [options] - Properties to augment the behaviour of clearAttributes()
     * @param {Boolean} [options.silent=false] - With silent enabled events will not be triggered
     * @returns {Promise} The AJAX call to Shopify's cart API.
     * @memberof Cart
     * @example
     * // Clears all cart attributes
     * theme.cart.clearAttributes();
     *
     * // Only clears the language attribute
     * theme.cart.clearAttributes('language');
     *
     * // Clears the language and currency attributes
     * theme.cart.clearAttributes(['language', 'currency']);
     */
    clearAttributes(attributes, { silent = false } = {}) {
      let cleared_attributes = {};

      // If no attributes are passed, use all cart attributes to clear
      const attribute_keys =
        attributes !== undefined
          ? [].concat(attributes || [])
          : Object.keys(theme.cart.content.attributes);

      for (let key of attribute_keys) {
        cleared_attributes[key] = "";
      }

      return this.updateAttributes(cleared_attributes, { silent: silent });
    }
  }

  // Global export
  // Uses the cart information stored by the global object if available
  theme.cart = new Cart(theme.cart || null);
})((window.theme = window.theme || {}), jQuery);

/**
 * PRODUCTS
 *
 * @description Keeps track of all the products on the page
 * @namespace products
 */

((theme, $) => {
  /**
   * Class representing the products object.
   */
  class Products {
    /**
     * Creates an instance of Products.
     *
     * @param {object} [products] - An object of product objects with the handle as the key.
     * @constructs Products
     * @memberof Products
     */
    constructor(products) {
      this._list = products || {};
    }

    // ------------------------------
    //  Getters and Setters
    // ------------------------------

    /**
     * The product list object.<br><br>
     * @example
     * // Get the product list
     * theme.products.list
     *
     * @type {object}
     * @memberof Products
     */
    get list() {
      return this._list;
    }

    set list(cart) {
      // Don't allow the list to be overwritten
      return false;
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Update the product list.
     * If the product doesn't exist, it will be added.
     * If the product exists, it will be updated. Parameters would be updated, not replaced.
     *
     * @param {(object|object[])} products - Product, object of products or array of products to add to the list.
     * @fires Products:add
     * @fires Products:change
     * @memberof Products
     * @example
     * // Examples use the following list:
     * // {
     * // 	product-a: { handle: 'product-a', title: 'Product A' },
     * // 	product-b: { handle: 'product-b', title: 'Product B' }
     * // }
     *
     * // Add 1 product
     * theme.products.update({ handle: 'product-c', title: 'Product C' });
     *
     * // Add product array
     * theme.products.update([
     * 	{ handle: 'product-c', title: 'Product C' },
     * 	{ handle: 'product-d', title: 'Product D' }
     * ]);
     *
     * // Add multiple product object
     * theme.products.update({
     * 	product-c: { handle: 'product-c', title: 'Product C' },
     * 	product-d: { handle: 'product-d', title: 'Product D' }
     * });
     *
     * // If the product exists, it will update the passed parameters
     * // Will result in product a having the following structure:
     * // { handle: 'product-a', title: 'Product A', price: 1200 }
     * theme.products.update({ handle: 'product-a', price: 1200 });
     */
    update(products) {
      if (!products || typeof products !== "object") {
        return false;
      }

      // Convert to array if single product is passed
      if (products.handle) {
        products = [products];
      } else if (!Array.isArray(products)) {
        // Convert object of products into array of products
        products = Object.keys(products)
          .map((key) => {
            return products[key];
          })
          .filter((product) => {
            return product.handle;
          });
      }

      products.forEach((product) => {
        if (product.handle) {
          // If the product doesn't exists, add it, otherwise update it
          if (!this._list[product.handle]) {
            this._list[product.handle] = product;

            /**
             * Products have been added to the global list.
             * The event is attached to the document element.<br><br>
             *
             * @event Products:add
             * @property {object} product_added - The added product
             * @memberof Products
             * @example
             * $(document).on('Products:add', function(event, product_added) {});
             */
            $(document).trigger("Products:add", product);
          } else {
            let product_updated = theme.utils.modifyObject(
              this._list[product.handle],
              product
            );
            this._list[product.handle] = product_updated;

            /**
             * Product has been updated on the global list.
             * The event is attached to the document element.<br><br>
             *
             * @event Products:update
             * @property {object} product_updated - The updated product
             * @memberof Products
             * @example
             * $(document).on('Products:update', function(event, product) {});
             */
            $(document).trigger("Products:update", product_updated);
          }
        }
      });
    }
  }

  // Global export and initialization
  // Uses the products stored by the global object if available
  theme.products = new Products(
    theme.products && theme.products.list ? theme.products.list : {}
  );
})((window.theme = window.theme || {}), jQuery);

/**
 * STICKY LIBRARY
 *
 * @description Handles sticky elements.
 * @namespace Sticky
 */

((theme, $) => {
  /**
   * Class representing the sticky object.
   *
   * @classdesc The Sticky class is used to create sticky objects out of elements with the class <code>js-sticky</code>. The <code>js-stuck</code> class is added once the element reaches the top of the document on scroll.
   * <br><br>
   * Breakpoints may be passed optionally as the data-sticky attribute, in json format
   * <br><br>
   * @example <div class="js-sticky"></div>
   * @example <div class="js-sticky" data-sticky='{"min":"768"}'></div>
   * @example <div class="js-sticky" data-sticky='{"min":"425","max":"768"}'></div>
   *
   * @property {object} [options] - Optional breakpoints
   * @property {integer} [options.max] - Disable sticky at screen sizes larger than this breakpoint; extracted from options
   * @property {integer} [options.min] - Disable sticky at screen sizes smaller than this breakpoint; extracted from options
   * @property {jQuery} $sticky - The sticky element
   * @property {boolean} disabled - Indicates if the sticky is disabled based on breakpoints
   * @property {integer} offset - Distance from the top of the element to the top of the document. Minimum 0.
   * @property {boolean} stuck - Indicates if the sticky is stuck
   */
  class Sticky {
    /**
     * Creates an instance of Sticky.
     *
     * @param {jQuery} $element - The sticky element
     * @constructs Sticky
     * @memberof Sticky
     */
    constructor($element) {
      if (!$element) {
        console.error("Sticky.constructor: No element was passed.");
        return false;
      }

      this.$sticky = $element;
      this.options = $element.data("sticky") || {};
      this.max =
        (this.options.hasOwnProperty("max") &&
          Math.floor(this.options.max) != this.options.max &&
          !$.isNumeric(this.options.max)) ||
        this.max < 0
          ? -1
          : this.options.max;
      this.min =
        (this.options.hasOwnProperty("min") &&
          Math.floor(this.options.min) != this.options.min &&
          !$.isNumeric(this.options.min)) ||
        this.options.min < 0
          ? -1
          : this.options.min;
      this.disabled = this._isDisabled();
      this.offset = Math.max($element.offset().top, 0);
      this.stuck = false;

      this._run();
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Internal method that handles the main functionality of the sticky object. Calls the <code>update()</code> on document scroll and <code>refresh()</code> on window resize.
     *
     * @private
     * @memberof Sticky
     */
    _run() {
      this.update();

      $(document).scroll(
        $.throttle(250, () => {
          this.update();
        })
      );

      $(window).resize(
        $.throttle(250, () => {
          this.refresh();
        })
      );
    }

    /**
     * Internal method that checks if the sticky is disabled based on breakpoints passed as options.
     *
     * @private
     * @memberof Sticky
     */
    _isDisabled() {
      if (this.max == -1 || this.min == -1) {
        return true;
      }
      return this.max < $(window).width() || this.min > $(window).width();
    }

    /**
     * Checks if the sticky element is disabled, then sticks or unsticks the element according to the offset.
     *
     * @memberof Sticky
     */
    update() {
      if (this.disabled) {
        return false;
      }
      $(document).scrollTop() > this.offset ? this.stick() : this.unstick();
    }

    /**
     * Calculates the offset of the sticky element from the top of the document and calls the update function if the element is not disabled.
     *
     * @memberof Sticky
     */
    refresh() {
      this.offset = Math.max(this.$sticky.offset().top, 0);
      this.disabled = this._isDisabled();
      if (this.disabled) {
        this.update();
      }
    }

    /**
     * Sticks the sticky element.
     *
     * @fires Sticky:stick
     * @memberof Sticky
     */
    stick() {
      /**
       * Adds the class <code>js-stuck</code> class to the sticky element.
       *
       * @event Sticky:stick
       * @memberof Sticky
       */
      this.stuck = true;
      this.$sticky.addClass("js-stuck").trigger("Sticky:stick");
    }

    /**
     * Unsticks the sticky element.
     *
     * @fires Sticky:unstick
     * @memberof Sticky
     */
    unstick() {
      /**
       * Removes the class <code>js-stuck</code> from the sticky element.
       *
       * @event Sticky:unstick
       * @memberof Sticky
       */
      this.stuck = false;
      this.$sticky.removeClass("js-stuck").trigger("Sticky:unstick");
    }
  }

  // ------------------------------
  //  Initiallization
  // ------------------------------

  theme.Sticky = Sticky;

  $(".js-sticky").each(function () {
    new theme.Sticky($(this));
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * ADDRESS LIBRARY
 *
 * @description Utilities to modify and create addresses
 * @namespace addresses
 */
((theme, $) => {
  const SELECTORS = {
    managerContainer: ".js-customer-addresses", // Element containing all address elements
    addressList: ".js-customer-address-list", // Container for iterated address containers
    addressContainer: ".js-customer-address", // Container of a single address
    add: {
      addContainer: ".js-address-add-container", // Container for the add address form
      errors: ".js-add-form-errors", // Container for add address errors
    },
    address: {
      form: ".js-customer-address-form", // Container for an individual address' edit form
      errors: ".js-update-form-errors", // Container for an individual address' form errors
      display: ".js-customer-address-display", // Container of textual address information
      delete: ".js-address-delete", // Address' delete button
      edit: ".js-customer-address-edit", // Address' edit button
      cancel: ".js-customer-address-cancel", // Address' cancel button
      primaryIndicator: ".js-customer-address-primary-indicator", // Primary address indicator
      empty: "js-customer-address-empty",
    },
  };

  /**
   * Utilities to provide account address update and creation
   * operations with AJAX
   *
   * @class
   */
  class AddressUtils {
    /**
     * Update an address with its id and serialized information.
     * Use when you already have address information in a serialized form.
     * Usually used in conjuction with jQuery's serialize() function.
     *
     * @param {Number} address_id - The address's id
     * @param {String} serializedForm - Data to be submitted to the address creation endpoint in a serialized format
     * @memberOf AddressUtils
     * @returns {jQuery.Deferred} Response from the ajax request
     * @example
     * addressUtils.updateAddressRaw(address_id, $form.serialize());
     */
    updateAddressRaw(address_id, serializedForm) {
      if (!address_id) {
        console.warn("No address id was provided.");
        return $.when();
      }

      return $.ajax({
        url: "/account/addresses/" + address_id,
        data: serializedForm,
        type: "POST",
        contentType: "application/x-www-form-urlencoded",
      });
    }

    /**
     * Update address information for an address with a specifc id.
     * Default parameters set variables to simulate a form.
     *
     * @param {Number} address_id - The address's id
     * @param {Object} address_data - Address information
     * @param {String} address_data.default - Pass the string "on" to make primary address
     * @param {String} address_data.first_name - First name
     * @param {String} address_data.last_name - Last name
     * @param {String} address_data.company - Company name for the address
     * @param {String} address_data.address1 - Address line 1
     * @param {String} address_data.address2 - Address line 2
     * @param {String} address_data.city - City
     * @param {String} address_data.province - Province or State
     * @param {String} address_data.country - Country
     * @param {String} address_data.zip - Zip or postal code
     * @param {String} address_data.phone - Phone number
     * @param {Object} [form_options] - Additional form options
     * @param {String} [form_options.form_type="customer_address"] - The Shopify form type
     * @param {String} [form_options.utf8="✓"] - UTF8 check
     * @param {String} [form_options._method="put"] - Submission method
     * @memberOf AddressUtils
     * @returns {jQuery.Deferred} Response from the ajax request
     * @example
     * addressUtils.updateAddress(address_id, address);
     */
    updateAddress(
      address_id,
      address_data,
      { form_type = "customer_address", utf8 = "✓", _method = "put" } = {}
    ) {
      let data = {
        form_type: form_type,
        utf8: utf8,
        address: address_data,
        _method: _method,
      };

      return this.updateAddressRaw(address_id, $.param(data));
    }

    /**
     * Created an address with the address information serialized
     * Usually used in conjuction with jQuery's serialize() function.
     *
     * @param {String} serializedForm - Data to be submitted to the address creation endpoint in a serialized format
     * @memberOf AddressUtils
     * @returns {jQuery.Deferred} Response from the ajax request
     * @example
     * addressUtils.createAddressRaw($form.serialize());
     */
    createAddressRaw(serializedForm) {
      return $.ajax({
        url: "/account/addresses/",
        data: serializedForm,
        type: "POST",
        contentType: "application/x-www-form-urlencoded",
      });
    }

    /**
     * Create a new address with the supplied address information.
     * Default parameters simulate a form.
     *
     * @param {Object} address_data - Address information
     * @param {String} address_data.default - Pass the string "on" to make primary address
     * @param {String} address_data.first_name - First name
     * @param {String} address_data.last_name - Last name
     * @param {String} address_data.company - Company name for the address
     * @param {String} address_data.address1 - Address line 1
     * @param {String} address_data.address2 - Address line 2
     * @param {String} address_data.city - City
     * @param {String} address_data.province - Province or State
     * @param {String} address_data.country - Country
     * @param {String} address_data.zip - Zip or postal code
     * @param {String} address_data.phone - Phone number
     * @param {Object} [form_options] - Additional form options
     * @param {String} [form_options.form_type="customer_address"] - The Shopify form type
     * @param {String} [form_options.utf8="✓"] - UTF8 check
     * @memberOf AddressUtils
     * @returns {jQuery.Deferred} Response from the ajax request
     * @example
     * addressUtils.createAddress(address);
     */
    createAddress(
      address_data,
      { form_type = "customer_address", utf8 = "✓" } = {}
    ) {
      let data = {
        form_type: form_type,
        utf8: utf8,
        address: address_data,
      };

      return this.createAddressRaw($.param(data));
    }

    /**
     * Delete an address by its id
     *
     * @param {Number|String} address_id - Address id to delete
     * @memberOf AddressUtils
     * @returns {jQuery.Deferred} Response from the ajax request
     * @example
     * addressUtils.deleteAddress(address_id);
     */
    deleteAddress(address_id) {
      return $.ajax({
        url: "/account/addresses/" + address_id,
        data: { _method: "delete" },
        type: "POST",
        contentType: "application/x-www-form-urlencoded",
      });
    }
  }

  /**
   * A manager class to keep track of address and
   * add new addresses
   *
   * @class
   * @prop {jQuery} $container - The container encompassing all address elements
   * @prop {jQuery} $addressList - The element containing all individual address containers
   * @prop {Address[]} addresses - List of client addresses
   */
  class AddressManager {
    /**
     * @param {jQuery} $container - The container for all address elements
     * @contructs AddressManager
     * @example
     * let addressManager = new AddressManager($('js-customer-addresses'));
     */
    constructor($container) {
      this.$container = $container;
      this.$addressList = $(SELECTORS.addressList, $container);

      // Initialize an Address for each of the client's addresses
      this.addresses = $(SELECTORS.addressContainer, this.$addressList)
        .get()
        .reduce(function (acc, cur) {
          let new_address = new Address($(cur));
          acc[new_address.id] = new_address;
          return acc;
        }, {});

      this.showAddressList();

      // Initialize elements for the new address form
      this._initAddAddress();
    }

    /**
     * Initialize elements and listeners for a new address form
     * @private
     */
    _initAddAddress() {
      let $add_container = $(SELECTORS.add.addContainer, this.$container);
      let $form = $("form", $add_container);
      let $errorContainer = $(SELECTORS.add.errors, $add_container);

      new Shopify.CountryProvinceSelector(
        "AddressCountry_new",
        "AddressProvince_new",
        { hideElement: "AddressProvinceContainer_new" }
      );

      $form.on("submit", (event) => {
        event.preventDefault();

        theme.addressUtils
          .createAddressRaw($form.serialize())
          .done((response) => {
            // reponse is a full html page that we will parse
            const $response = $(response);
            const errors = $(SELECTORS.add.errors, $response).html();

            // Copy any errors from the response
            if (errors) {
              $errorContainer.html(errors);
            } else {
              location.reload();
            }
          });
      });
    }

    /**
     * Extracts new address information from an address AJAX request and
     * creates the new address elements
     *
     * @param {jQuery} $address_reponse - A jQuery element (usually in shadow DOM) that contains reponse elements
     * @private
     */
    _addAddress($address_response) {
      let $container = $(SELECTORS.managerContainer, $address_response);
      $(SELECTORS.addressContainer, $container).each((index, element) => {
        let $element = $(element);

        // Determine if the address exists already, add the its element and
        // create the Address if it it new
        if (!this.addresses[$element.data("address-id")]) {
          $element.appendTo(this.$addressList);
          let new_address = new Address($element);
          this.addresses[new_address.id] = new_address;
        }
      });
    }

    /**
     * Remove an address from the manager.
     *
     * @param {Number} address_id - The address' id
     */
    removeAddress(address_id) {
      delete this.addresses[address_id];
    }

    /**
     * Update the primary address, remove styling from
     * the old primary and setup the new one
     * @param {Address} address - Address to make primary
     */
    updatePrimary(address) {
      // Remove primary from the existing primary
      Object.values(this.addresses)
        .filter((address) => address.primary)
        .forEach((address) => address.removePrimary());

      // Make the new address primary
      address.makePrimary();
    }

    showAddressList() {
      let $empty_address_list = $(".js-customer-address-empty");
      let address_count = $(".js-customer-address").length || 0;

      if (address_count == 0) {
        $empty_address_list.show();
      } else {
        $empty_address_list.hide();
      }
    }
  }

  /**
   * A single address with buttons to show/hide the edit form,
   * buttons to update and delete the address.
   * An Address also keeps track if it primary or becomes primary
   * and updates the AddressManager.
   *
   * @class
   * @prop {Number} id - The address id
   * @prop {jQuery} $addressContainer - The container for the displayed address information
   * @prop {jQuery} $primaryAddressInput - The checkbox to set this address as primary
   */
  class Address {
    /**
     * @param {jQuery} $container - The container of all elements for this address
     * @constructs Address
     */
    constructor($container) {
      this.id = $container.data("address-id");

      let $formContainer = $(SELECTORS.address.form, $container);
      let $form = $("form", $formContainer);
      let $errorContainer = $(SELECTORS.address.errors, $form);

      this.$addressContainer = $(SELECTORS.address.display, $container);
      this.$addressListContainer = $(SELECTORS.addressList);
      this.$primaryAddressInput = $("#address_default_" + this.id, $form);
      this._primary = !!$container.data("primary");

      new Shopify.CountryProvinceSelector(
        "AddressCountry_" + this.id,
        "AddressProvince_" + this.id,
        { hideElement: "AddressProvinceContainer_" + this.id }
      );

      $form.on("submit", (event) => {
        event.preventDefault();

        theme.addressUtils
          .updateAddressRaw(this.id, $form.serialize())
          .done((response) => {
            // Extract error and new address information for this address from the response
            const $response = $(response);
            const $response_container = $(
              `${SELECTORS.addressContainer}[data-address-id="${this.id}"]`,
              $response
            );
            const errors = $(
              SELECTORS.address.errors,
              $response_container
            ).html();
            const address_display = $(
              SELECTORS.address.display,
              $response_container
            ).html();
            const primary = !!$response_container.data("primary");

            // Copy errors
            if (errors) {
              $errorContainer.html(errors);
            } else {
              $errorContainer.empty();
              this.$addressListContainer.trigger("Addresses:update-valid");
            }

            // Update the address
            if (address_display) {
              this.$addressContainer.html(address_display);
              $container.trigger("Addresses:update-valid");
              this._bindDeleteButton($container);
            }

            // Update the primary state if required
            if (!this._primary && primary) {
              theme.addressManager.updatePrimary(this);
            }

            theme.addressManager.showAddressList();
          });
      });

      this._bindDeleteButton($container);

      // Show form when clicking edit button
      $(SELECTORS.address.edit, $container).on("click", (event) => {
        $formContainer.show();
      });

      // Hide form when clicking cancel button
      $(SELECTORS.address.cancel, $form).on("click", (event) => {
        $formContainer.hide();
      });
    }

    _bindDeleteButton($container) {
      let $formContainer = $(SELECTORS.address.form, $container);

      $(SELECTORS.address.delete, $container).on("click", (event) => {
        event.preventDefault();
        let delete_confirmation = $(
          ".js-address-delete-message",
          $container
        ).data("address-delete-message");

        if (window.confirm(delete_confirmation)) {
          theme.addressUtils.deleteAddress(this.id).done((response) => {
            // Remove the DOM element
            $container.remove();

            // Remove this address from the manager
            theme.addressManager.removeAddress(this.id);

            theme.addressManager.showAddressList();

            $formContainer.trigger("Addresses:delete");
          });
        }
      });
    }

    /**
     * Wether this address is the primary address
     *
     * @type {Boolean}
     */
    get primary() {
      return this._primary;
    }

    /**
     * Make this address primary
     */
    makePrimary() {
      this._primary = true;
      this.$primaryAddressInput.prop("checked", true);
    }

    /**
     * Make this address not primary
     */
    removePrimary() {
      this._primary = false;
      this.$primaryAddressInput.prop("checked", false);
      $(SELECTORS.address.primaryIndicator, this.$addressContainer).remove();
    }
  }

  $(() => {
    let $addressContainer = $(SELECTORS.managerContainer);
    if ($addressContainer.length > 0) {
      theme.addressUtils = new AddressUtils();
      theme.addressManager = new AddressManager($addressContainer.eq(0));
    }
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * FLYOUT LIBRARY
 *
 * @description Handles flyout elements.
 * @namespace Flyout
 */
((theme, $) => {
  /**
   * Class names.
   *
   * @private
   * @const
   * @type {Object}
   */
  const CLASSES = {
    noScroll: "u-no-scroll",
    flyoutActive: {
      left: "flyout-active-left",
      right: "flyout-active-right",
    },
    flyoutHidden: {
      left: "flyout-hidden-left",
      right: "flyout-hidden-right",
    },
  };

  /**
   * Class representing a flyout element.
   *
   * @classdesc Creates Flyout objects out of elements with the class <code>js-flyout</code> and the data attribute <code>data-flyout-id</code>, where the value is a unique id. The element can optionally have a <code>data-flyout-options</code> attribute, where the value is a JSON object that can have
   *<br><br>
   * 1) a <code>max</code> and/or <code>min</code> property with a value, indicating the maximum or minimum screensize (in pixels) on which the flyout will be enabled.
   * <br>
   * 2) a <code>direction</code> property with the value <code>left</code> or <code>right</code>, which indicates which side the flyout will display from. The default direction is <code>right</code>.
   * <br><br>
   * A Flyout object requires trigger elements to open/close the flyout, with the data attribute <code>data-flyout-open</code>, <code>data-flyout-close</code>, or <code>data-flyout-toggle</code>. The value of the data attribute must be the data-flyout-id of the flyout element being targeted
   *
   * @example
   * <div class="flyout js-flyout" data-flyout-id="my-flyout" data-flyout-options="{'direction':'right','max':500,'min':200}">
   * This Is My Flyout
   * </div>
   * <button data-flyout-toggle="my-flyout">Toggle Flyout</button>
   *
   * @property {object} elements - Contains the flyout and its triggers
   * @property {string} id - The flyout ID
   * @property {json} options - options for the flyout
   * @property {string} [options.direction] - direction from which the flyout will appear
   * @property {string} [options.max] - max screen width at which this will be enabled
   * @property {string} [options.min] - min screen width at which this will be enabled
   * @property {string} direction - Optional direction extracted from options; default is right
   * @property {string} max - Optional max extracted from options
   * @property {string} min - Optional min extracted from options
   */

  class Flyout {
    /**
     * Creates an instance of Flyout.
     *
     * @param {jQuery} $flyout - The flyout element.
     * @constructs Flyout
     * @memberof flyout
     */
    constructor($flyout) {
      if (!$flyout) {
        console.error("Flyout.constructor: No element was passed.");
        return false;
      }

      this.elements = {
        flyout: $flyout,
      };
      this.id = $flyout.data("flyout-id") || false;
      this.options = $flyout.data("flyout-options") || false;

      this.direction =
        this.options.direction &&
        (this.options.direction == "left" || this.options.direction == "right")
          ? this.options.direction
          : "right";

      this.max =
        (this.options.max &&
          Math.floor(this.options.max) != this.options.max &&
          !$.isNumeric(this.options.max)) ||
        this.max < 0
          ? -1
          : this.options.max;
      this.min =
        (this.options.min &&
          Math.floor(this.options.min) != this.options.min &&
          !$.isNumeric(this.options.min)) ||
        this.options.min < 0
          ? -1
          : this.options.min;
      this.show_when_disabled = this.options.show_when_disabled || false;

      this.disabled = this._isDisabled();
      this.hidden = false;

      if (this.id) {
        this._bindTriggers();
        this._addRoles();
        this._updateFlyoutDisable();
        this._hideFlyout();
        this._run();
      }
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     *	Listens and re-checks on window resize to see if the flyout should be disabled
     *
     * @private
     * @memberof Flyout
     */
    _run() {
      $(window).resize(() => {
        this._refresh();
      });
    }

    /**
     * checks to see if flyout should be disabled, rebinds triggers, hides the flyout.
     *
     * @private
     * @memberof Flyout
     */
    _refresh() {
      this.disabled = this._isDisabled();
      this._bindTriggers();
      this._addRoles();
      this._updateFlyoutDisable();
    }

    /**
     * Checks if window size is smaller than min or larger than max.
     *
     * @private
     * @memberof Flyout
     */
    _isDisabled() {
      if (this.max == -1 || this.min == -1) {
        return true;
      }
      return this.max <= $(window).width() || this.min >= $(window).width();
    }

    /**
     * Adds accessibility roles to a trigger element.
     *
     * @param {jQuery} $elem - The trigger to check for accessibility roles.
     * @private
     * @memberof Flyout
     */
    _addRole($elem) {
      if (
        $elem.prop("tagName") != "Button" &&
        $elem.attr("type") != "submit" &&
        !$elem.attr("role") &&
        !$elem.attr("aria-role")
      ) {
        $elem.attr("role", "button");
        $elem.attr("aria-role", "button");
      }
    }

    /**
     * Adds accessibility roles to all trigger elements. Must be run after this._bindTriggers.
     *
     * @private
     * @memberof Flyout
     */
    _addRoles() {
      $([this.elements.toggle, this.elements.open, this.elements.close]).each(
        (index, element) => {
          this._addRole($(element));
        }
      );
    }

    /**
     * Internal method that binds flyout triggers, based on the flyout element's ID, and adds them to <code>flyout.elements</code>. Handles click events. Note that trigger elements which are loaded through AJAX will have to be re-bound.
     *
     * @private
     * @memberof Flyout
     */
    _bindTriggers() {
      this.elements.toggle = $.find("[data-flyout-toggle=" + this.id + "]");
      this.elements.open = $.find("[data-flyout-open=" + this.id + "]");
      this.elements.close = $.find("[data-flyout-close=" + this.id + "]");

      if (this.disabled) {
        $(this.elements.toggle).off("click.flyout");
        $(this.elements.open).off("click.flyout");
        $(this.elements.close).off("click.flyout");
        return false;
      }

      this.elements.flyout.on("click.flyout", (event) =>
        event.stopPropagation()
      );

      $(this.elements.toggle)
        .off("click.flyout")
        .on("click.flyout", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggle();
        });

      $(this.elements.open)
        .off("click.flyout")
        .on("click.flyout", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open();
        });

      $(this.elements.close)
        .off("click.flyout")
        .on("click.flyout", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        });
    }

    /**
     * Internal method that hides the flyout element based on the direction it will open from.
     *
     * @private
     * @memberof Flyout
     */
    _hideFlyout() {
      if (!this.disabled && !this.hidden) {
        this.elements.flyout
          .removeClass(CLASSES.flyoutActive[this.direction])
          .addClass(CLASSES.flyoutHidden[this.direction]);

        this.hidden = true;
        this.elements.flyout.trigger("Flyout:closing").trigger("Flyout:close");
      }
    }

    /**
     * Internal method that disables the flyout if required
     *
     * @private
     * @memberof Flyout
     */
    _updateFlyoutDisable() {
      if (this.disabled) {
        this.elements.flyout.removeClass(CLASSES.flyoutActive[this.direction]);

        if (this.show_when_disabled) {
          this.elements.flyout
            .removeClass(CLASSES.flyoutHidden[this.direction])
            .show();
        } else {
          this.elements.flyout
            .hide()
            .addClass(CLASSES.flyoutHidden[this.direction]);
        }

        this.hidden = true;
        this.elements.flyout.trigger("Flyout:closing").trigger("Flyout:close");
      } else {
        this.elements.flyout.show();

        if (this.hidden) {
          this.elements.flyout.addClass(CLASSES.flyoutHidden[this.direction]);
        }
      }
    }

    /**
     * Opens the flyout in the specified direction. Adds the class <code>flyout-active-${direction}</code> to the flyout element, removes the class <code>flyout-hidden-${direction}</code>.
     *
     * @fires Flyout:open
     * @memberof Flyout
     */
    open() {
      /**
       * A flyout element has been opened. Event is attached to a .js-flyout element.
       *
       * @event Flyout:open
       * @memberof Flyout
       * @example
       * $(.js-flyout).on('Flyout:open', function(event) { });
       */

      $("body").addClass("flyout__" + this.id + "--open");
      $("body").on("click.flyout", () => this.close());

      this.elements.flyout
        .removeClass(CLASSES.flyoutHidden[this.direction])
        .addClass(CLASSES.flyoutActive[this.direction])
        .show()
        .trigger("Flyout:opening")
        .on(
          "transitionend webkittransitionEnd otransitionEnd MStransitionEnd",
          function (e) {
            $(this).trigger("Flyout:open");
            $(this).off(e);
          }
        );

      this.hidden = false;
    }

    /**
     * Closes the flyout in the opposite direction from which it opened.
     * Removes the <code>flyout-active-${direction}</code> class from the flyout element, adds the <code>flyout-hidden-${direction}</code> class.
     *
     * @fires Flyout:close
     * @memberof Flyout
     */
    close() {
      $("body").removeClass("flyout__" + this.id + "--open");
      $("body").off("click.flyout");
      this.elements.flyout
        .removeClass(CLASSES.flyoutActive[this.direction])
        .addClass(CLASSES.flyoutHidden[this.direction])
        .trigger("Flyout:closing");

      /**
       * A flyout element has been closed. Event is attached to a .js-flyout element.
       *
       * @event Flyout:close
       * @memberof Flyout
       * @example
       * $(.js-flyout).on('Flyout:close', function(event) { });
       */

      this.elements.flyout.on(
        "transitionend webkittransitionEnd otransitionEnd MStransitionEnd",
        function (e) {
          $(this).off(e);
          $(this).trigger("Flyout:close");
        }
      );
      this.hidden = true;
    }

    /**
     * Toggles the flyout depending on its current state.
     *
     * @memberof Flyout
     * @fires Flyout:toggle
     */
    toggle() {
      /**
       * A flyout element has been toggled. Event is attached to a .js-flyout element.
       *
       * @event Flyout:toggle
       * @memberof Flyout
       * @example
       * $(.js-flyout).on('Flyout:toggle', function(event) { });
       */

      this.elements.flyout.trigger("Flyout:toggle");

      if (!this.hidden) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  // ------------------------------
  //  Initialization
  // ------------------------------

  theme.Flyout = Flyout;

  $(".js-flyout").each(function () {
    new theme.Flyout($(this));
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * DROPDOWN LIBRARY
 *
 * @description Handles all dropdown functionality.
 * @namespace theme.Dropdown
 */
((theme, $) => {
  /**
   * Default settings.
   *
   * @private
   * @const
   * @type {Object}
   */
  const DEFAULTS = {
    closeMenuOnSelect: false,
    showSelectionInLabel: true,
  };

  /**
   * Class selectors.
   *
   * @private
   * @const
   * @type {Object}
   */
  const CLASSES = {
    active: "active",
    open: "open",
  };

  /**
   * Creates an DropdownOption object representing an option in a dropdown list.
   *
   * @classdesc will be accessed by Dropdown class constructor
   * @property {string} value - The html content of $element
   * @property {jQuery[]} elements - The DOM elements constituting the DropdownOption. Generally has only one item.
   * @property {jQuery} elements.option - The main DOM element.
   */
  class DropdownOption {
    /**
     * Creates an instance of DropdownOption.
     *
     * @param {jQuery} $element - The DOM element to initialize on.
     * @param {number} [id] - The id to add to the element.
     * @constructs DropdownOption
     * @memberof DropdownOption
     */
    constructor($element, id) {
      if (!$element) {
        return false;
      }

      this.value = $element.html();

      this.elements = {
        option: $element,
      };

      // Assign a unique id to the option
      if (id) {
        this.elements.option.data("option-id", id);
      }

      // Add event listeners that calls set()
      this.elements.option.off("click.Dropdown").on("click.Dropdown", () => {
        this.set();
      });
    }

    /**
     * Is set to happen on click, triggers an event.
     *
     * @fires DropdownOption:set
     * @memberof DropdownOption
     */
    set() {
      this.elements.option.trigger("DropdownOption:set", this.value);
    }
  }

  /**
   * Class representing the dropdown.
   *
   * @classdesc makes Dropdowns. <br><br>
   * Usage: executed on a dropdown container element with the class <code>js-dropdown</code>, containing
   * <ol>
   * 	<li> a label element with the class <code>js-dropdown-label</code> </li> <li> a list element with the class <code>js-dropdown-list</code>, or a  <code>ul</code> element. Must contain child elements with the class <code>js-dropdown-option</code>, or <code>li</code> elements.</li>
   * 	<li>Can also contain an optional trigger element with the class <code>js-dropdown-trigger</code> which will toggle the dropdown menu open and closed- if none are found, this will default to the label element.</li>
   * </ol>
   * <br><br>
   * The <code>js-dropdown</code> container element can optionally have a data-dropdown-settings attribute in JSON format, to set behaviour for this dropdown. Consult constructor documentation for setting names and values.
   *
   * @example
   * <div class="js-dropdown">
   * 	<span class="js-dropdown-label">Dropdown Title</span>
   * 	<ul class="js-dropdown-list">
   * 		<li class="js-dropdown-option">Option 1</li>
   * 		<li class="js-dropdown-option">Option 2</li>
   * 		<li class="js-dropdown-option">Option 3</li>
   * 	</ul>
   * </div>
   *
   * @property {boolean} expanded - Boolean which determines whether the dropdown is open or closed.
   * @property {object} options - Keeps track of the dropdown's options, represented as DropdownOption instances.
   * @property {array} elements - An array of DOM elements that are parts of the dropdown.
   * @property {jQuery} elements.dropdown - The dropdown container DOM element
   * @property {jQuery} elements.list - The dropdown list DOM element, identified by class '.js-dropdown-list'. If none are found, it will be set to any <code>ul</code> child element of the $dropdown container element.
   * @property {jQuery} elements.label - The dropdown label DOM element, identified by class '.js-dropdown-label'.
   * @property {jQuery} [elements.trigger=elements.label] - The DOM element to trigger the menu to open or close, identified by class '.js-dropdown-trigger'. If not set,default will be the label element.
   * @property {jQuery} $selected - Holds the DOM element which displays the currently selected option.
   * @property {Option} _selected_option - Holds the DropdownOption instance corresponding to the currently selected option
   */
  class Dropdown {
    /**
     * Creates an instance of Dropdown.
     *
     * @constructs Dropdown
     * @param {jQuery} $dropdown - The dropdown container DOM element.
     * @param {object} settings - settings to be set on the dropdown. Derived from, in order of descending importance: The data-dropdown-settings attribute of the $dropdown element, the settings passed into the constructor, and the default settings.
     * @param {boolean} [settings.closeMenuOnSelect = false] - if true, menu will close when an option is chosen.
     * @param {boolean} [settings.showSelectionInLabel = true] - if true, label will change to show currently selected option.
     * @memberof Dropdown
     */
    constructor($dropdown, settings) {
      this.expanded = false;
      this._selected_option;

      let dropdownSettings = $dropdown.data("dropdown-settings");
      this.settings = $.extend({}, DEFAULTS, settings, dropdownSettings);

      this.elements = {
        dropdown: $dropdown,
        list: $dropdown.find(".js-dropdown-list").length
          ? $dropdown.find(".js-dropdown-list")
          : $dropdown.find("ul"),
        label: $dropdown.find(".js-dropdown-label"),
        trigger: $dropdown.find(".js-dropdown-trigger"),
      };

      // If no trigger is specified, use the label
      if (this.elements.trigger.length === 0) {
        this.elements.trigger = this.elements.label;
      }

      this.bindOptions();

      // Trigger toggle event handler
      this.elements.trigger.off("click.Dropdown").on("click.Dropdown", () => {
        this.toggle();
      });

      // When clicking away from the dropdown, close this menu if it's expanded.
      $(document).on("click.Dropdown", (e) => {
        let $clicked_elements = $(e.target).parents().addBack();
        if (!$clicked_elements.is(this.elements.dropdown)) {
          if (this.expanded) {
            this.close();
          }
        }
      });
    }

    // ------------------------------
    //  Getters and Setters
    // ------------------------------

    /**
     * The DOM element corresponding to the currently selected dropdown option. Has a setter and a getter.
     * The setter expects a DOM element as argument, and will also set this._selected_option, add the 'active' class to the DOM element, and change the label DOM element to display the same HTML as the selected element.
     *
     * @type {jQuery}
     * @fires Dropdown:update
     * @memberof Dropdown
     */
    set $selected($option) {
      if (!$option || !$option.data("option-id")) {
        return false;
      }

      let option_id = $option.data("option-id");
      // Get the DropdownOption object in this.options with the same id
      let option = this.options[option_id];

      // update this._selected_option to that DropdownOption Object
      this._selected_option = option;

      // Updates active classes on the options
      this.elements.options.not($option).removeClass(CLASSES.active);
      option.elements.option.addClass(CLASSES.active);

      // Change label to display the clicked value
      if (this.settings.showSelectionInLabel) {
        this.elements.label.html(option.value);
      }

      // Fire an event that returns the DropdownOption object
      this.elements.dropdown.trigger("Dropdown:update", option);
    }

    /**
     * The currently selected option has changed.
     *
     * @event Dropdown:update
     */
    get $selected() {
      return this._selected_option;
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Consolidates all the list options on the dropdown, and populates this.options with DropdownOption objects.
     *
     * @memberof Dropdown
     */
    bindOptions() {
      let _ = this;
      this.options = {};
      this.elements.options = this.elements.list.find(".js-dropdown-option")
        .length
        ? this.elements.list.find(".js-dropdown-option")
        : this.elements.list.find("li");
      this.elements.options.each((index, element) => {
        let id = theme.utils.uniqueNumber();
        this.options[id] = new DropdownOption($(element), id);
      });

      /*
       * When DropdownOption fires its DropdownOption:set event from a click on an option element, set this.$selected to that option element.
       * See also: the setter function for $selected, which will execute as a result of this.
       * If closeMenuOnSelect is true, this will also close the menu.
       */
      this.elements.options.on("DropdownOption:set", function () {
        if (_.settings.closeMenuOnSelect) {
          _.close();
        }
        if (!$(this).hasClass("js-option-disabled")) {
          _.$selected = $(this);
        }
      });
    }

    /**
     * Opens the dropdown menu.
     *
     * @fires Dropdown:open
     * @memberof Dropdown
     */
    open() {
      this.expanded = true;
      this.elements.list.slideDown();

      /**
       * The dropdown element has opened.
       *
       * @event Dropdown:open
       */
      this.elements.dropdown.addClass(CLASSES.open).trigger("Dropdown:open");
      this.elements.dropdown.find(".menu").addClass(CLASSES.open);
    }

    /**
     * Closes the dropdown menu.
     *
     * @fires Dropdown:close
     * @memberof Dropdown
     */
    close() {
      this.expanded = false;
      this.elements.list.slideUp();

      /**
       * The dropdown element has closed.
       *
       * @event Dropdown:close
       */

      this.elements.dropdown
        .removeClass(CLASSES.open)
        .trigger("Dropdown:close");
      this.elements.dropdown.find(".menu").removeClass(CLASSES.open);
    }

    /**
     * Determines whether the dropdown menu should be opened or closed.
     *
     * @memberof Dropdown
     * @fires Dropdown:toggle
     */
    toggle() {
      if (this.expanded) {
        this.close();
      } else {
        this.open();
      }

      /**
       * The dropdown element has been toggled.
       *
       * @event Dropdown:toggle
       */
      this.elements.dropdown.trigger("Dropdown:toggle");
    }
  }

  // Global export.
  theme.Dropdown = Dropdown;

  // Initialize dropdowns on the page.
  $(".js-dropdown").each(function () {
    new Dropdown($(this));
  });
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  class Modal {
    constructor($modal) {
      if (!$modal) {
        console.error("Modal.constructor: No element was passed.");
        return false;
      }

      this.elements = {
        $wrapper: $modal,
        $content: $modal.find(".js-modal-content"),
      };

      this.id = $modal.attr("id") || false;

      this._bindTriggers();
    }

    _bindTriggers() {
      this.elements.open = $(`[data-modal-open=${this.id}]`);
      this.elements.close = this.elements.$wrapper.find(
        `[data-modal-close=${this.id}]`
      );

      this.elements.$wrapper.off("click.modal").on("click.modal", (event) => {
        let isModalContent =
          this.elements.$content.is(event.target) ||
          !!this.elements.$content.has(event.target).length;

        if (!isModalContent) {
          this.close();
        }
      });

      if (this.disabled) {
        $(this.elements.open).off("click.modal");
        $(this.elements.close).off("click.modal");
        return false;
      }

      $(this.elements.open)
        .off("click.modal")
        .on("click.modal", (event) => {
          event.preventDefault();
          this.open();
        });

      $(this.elements.close)
        .off("click.modal")
        .on("click.modal", (event) => {
          event.preventDefault();
          this.close();
        });

      let $form = this.elements.$wrapper.find("form");
      if ($form.length) {
        $form.on("submit", () => {
          this.close();
        });
      }
    }

    open() {
      this.elements.$wrapper.fadeIn();
      $(document).trigger("Modal:open", this.elements.$wrapper);
      $("body")
        .css(
          "top",
          -Math.max(document.body.scrollTop, document.documentElement.scrollTop)
        )
        .addClass("u-no-scroll");
    }

    close() {
      this.elements.$wrapper.fadeOut();
      $(document).trigger("Modal:close", this.elements.$wrapper);
      let scrollTop = Math.abs(parseInt($("body").css("top")));
      $("body").removeClass("u-no-scroll").css("top", 0);
      document.body.scrollTop = document.documentElement.scrollTop = scrollTop;
    }
  }

  theme.Modal = Modal;

  $(".js-modal:not([data-modal-omit])").each(function () {
    new theme.Modal($(this));
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * VARIANT SELECTOR LIBRARY
 *
 * @description Handles selection of variants, determines state of a selection, and broadcasts related events
 * @namespace variantSelector
 */

((theme, $) => {
  /**
   * The VariantSelector manages attaching events and triggers between
   * variant selector elements and the variant selector engine.
   * Settings can be specified in the constructor or a 'data-variant-selector' attribute
   * on the container.
   * @class
   * @example
   * //<div class="js-my-variant-selector" data-variant-selector="{ update_atc: false }">
   * //    <!-- ... -->
   * //</div>
   * let variant_selector = new VariantSelector($('.js-my-variant-selector'), { selectors: { atc_button: '.js-my-atc' }});
   */
  class VariantSelector {
    /**
     *
     * @param {jQuery} $container - The container encompassing all variant selector DOM elements
     * @param {Object} [settings] - Configurable Variant Selector settings
     * @param {Object} [settings.selectors] - Selectors for selecting DOM elements
     * @param {string} [settings.selectors.option_selector='.js-option-selector'] - Input elements for selecting a variant option's value.
     * The option's name should be a in the name attribute and its value should be in the value attribute of the input.
     * @param {string} [settings.selectors.id_field='[name="id"]'] - Selector for form elements specifying the selected variant by id.
     * This is usually some sort of input element with name="id".
     * @param {string} [settings.selectors.$atc_button='[type="submit"]'] - Selector for the Add to Cart button.
     * This is usually the submit button for your form. It will get enabled/disabled based on the selected variant and whether it is in stock.
     * @param {Boolean} [settings.update_atc=true] - Whether the atc button show be enabled/disabled
     * @param {Boolean} [settings.update_id_field=true] - Whether the id field should be updated
     * @param {Boolean} [settings.force_id_update=false] - (update_id_field must be true) The variant selector will always try to set the first possible variant event if all the options have not been selected
     * @param {boolean} [settings.reset_selection=false] - Whether to reset less significant options when a more significant one is selected
     * @constructs VariantSelector
     *
     */
    constructor($container, settings) {
      const defaults = {
        selectors: {
          option_selector: ".js-option-selector",
          id_field: '[name="id"]',
          atc_button: '[type="submit"]',
        },
        update_atc: true,
        update_id_field: true,
        force_id_update: false,
        reset_selection: false,
      };

      this.$container = $container;

      let data_options = this.$container.data("variant-selector");
      this.options = $.extend(true, {}, defaults, data_options, settings);

      this.elements = {
        $id_field: $(this.options.selectors.id_field, this.$container),
        $atc_button: $(this.options.selectors.atc_button, this.$container),
        $option_selectors: $(
          this.options.selectors.option_selector,
          this.$container
        ),
      };

      // Determine whether the id field is a set of radio buttons
      // and should be checked
      let id_field_type = this.elements.$id_field.attr("type");
      this.check_id_field =
        id_field_type && id_field_type.toLowerCase() === "radio";

      if (this.options.update_atc) {
        this.$container.on(
          "variant-selector__selection-complete",
          (event, variant) => {
            if (this.options.update_id_field) {
              this.setIdField(variant.id);
            }

            if (
              variant.available &&
              (variant.inventory_policy == "continue" ||
                variant.inventory_quantity > 0)
            ) {
              this.enableAtc();
            } else {
              this.disableAtc();
            }
          }
        );
        this.$container.on("variant-selector__selection-illegal", () =>
          this.disableAtc()
        );

        this.$container.on(
          "variant-selector__selection-incomplete",
          (event, variants) => {
            if (this.options.force_id_update && variants.length > 0) {
              this.setIdField(variants[0].id);
            }

            this.disableAtc();
          }
        );
      } else if (
        this.options.update_id_field &&
        !this.options.force_id_update
      ) {
        this.$container.on(
          "variant-selector__selection-complete",
          (event, variant) => {
            this.setIdField(variant.id);
          }
        );
      } else if (this.options.force_id_update) {
        this.$container.on(
          "variant-selector__selection-update",
          (event, variants) => {
            if (variants.length > 0) {
              this.setIdField(variants[0].id);
            }
          }
        );
      }

      if (this.elements.$option_selectors.length > 0) {
        // Create the variant selector object with associated fields and selected values
        this.variant_selector = new VariantSelectorEngine({
          product_handle: this.$container.data("product-handle"),
          selected_values: this.elements.$option_selectors
            .get()
            .reduce((acc, cur) => {
              if (cur.checked) {
                acc[cur.getAttribute("name")] = cur.value;
              }
              return acc;
            }, {}),
          $event_root: this.$container,
          reset_selection: this.options.reset_selection,
        });

        // Set the PDP's (Product Display Page's) radio buttons or select dropdowns as triggers for the variant selector
        this.elements.$option_selectors.each((index, element) => {
          if (element.tagName === "SELECT") {
            this.variant_selector.addTrigger(
              $(element),
              "change",
              element.getAttribute("name"),
              () => element.value
            );
          } else {
            this.variant_selector.addTrigger(
              $(element),
              "change",
              element.getAttribute("name"),
              element.value
            );
          }
        });
      } else {
        // Create the variant selector object with associated fields and selected values
        this.variant_selector = new VariantSelectorEngine({
          product_handle: this.$container.data("product-handle"),
          $event_root: this.$container,
          reset_selection: this.options.reset_selection,
        });

        // When we've selected a variant
        this.elements.$id_field.on("change", (event) =>
          this.variant_selector.setVariant(parseInt(event.target.value))
        );

        // Set the initial selection
        if (!this.check_id_field) {
          this.variant_selector.setVariant(
            parseInt(this.elements.$id_field.val())
          );
        } else {
          let $selected_radio_button =
            this.elements.$id_field.filter(":checked");

          if ($selected_radio_button.length > 0) {
            this.variant_selector.setVariant(
              parseInt($selected_radio_button.val())
            );
          }
        }
      }
    }

    /**
     * Enable the Add to Cart button
     * @memberof VariantSelector
     */
    enableAtc() {
      this.elements.$atc_button.prop("disabled", false);
    }

    /**
     * Disable the Add to Cart button
     * @memberof VariantSelector
     */
    disableAtc() {
      this.elements.$atc_button.prop("disabled", true);
    }

    /**
     * Update the form's id field with the currently selected variant
     * @param {number} variantId - The selected variant
     * @memberof VariantSelector
     */
    setIdField(variantId) {
      if (this.check_id_field) {
        this.elements.$id_field
          .filter((index, element) => parseInt(element.value) === variantId)
          .prop("checked", true);
      } else {
        this.elements.$id_field.val(variantId);
      }
    }
  }

  /**
   * Variant selector class holds the product and its
   * variants, determines state of the variant selector based
   * on selected options
   * @example
   * // Example construction configurations
   * let settings = {};
   * settings.product_handle = 'green-top';
   * settings.selected_values = {
   *     'color': $('.product__form-swatches:checked')
   * };
   *
   * let variant_selector = new VariantSelectorEngine(settings);
   */
  class VariantSelectorEngine {
    /**
     * @constructs VariantSelectorEngine
     * @memberof VariantSelectorEngine
     * @param {Object} settings - Variant Selector settings
     * @param {string} settings.product_handle - Product handle for this variant selector
     * @param {Object.<string, string>} [settings.selected_values] - A map of option names to values {"option_name": "value", ... } to indicates preselected values
     * @param {jQuery} [settings.$event_root=$()] - Root element for triggering events
     * @param {boolean} [settings.reset_selection=false] - Whether to reset less significant options when a more significant one is selected
     */
    constructor(settings) {
      this.settings = settings;
      this.product = theme.products.list[settings.product_handle];
      this.$event_root = settings.$event_root || $();
      this.reset_selection = settings.reset_selection || false;
      this.option_order = [];

      // Create a map of option_names to option values
      this.variant_options = this.product.options_with_values.reduce(
        (acc, cur) => {
          acc[cur.name] = new VariantOption(cur, this.$event_root);
          return acc;
        },
        {}
      );

      if (this.reset_selection) {
        this.option_order = this.product.options_with_values.map(
          (option) => option.name
        );
      }

      if (this.settings.selected_values) {
        // Pre-select default options
        Object.keys(this.settings.selected_values).forEach(
          (option) =>
            (this.variant_options[option].selected =
              this.settings.selected_values[option])
        );

        this.updateVariantSelector();
      }
    }

    /**
     * A getvalue function can be passed to dynamically determine the value of a trigger
     * @callback getOptionTriggerValue
     * @param {jQuery} $element - The DOM element triggering the option change
     * @returns {string} The selected value
     */

    /**
     * Add a trigger to select a specific value for an option
     * @param {jQuery} $element - DOM element triggering an event
     * @param {string} event - Event name
     * @param {string} name - Option name being updated
     * @param {(string|getOptionTriggerValue)} value - Can be a string or a callback function to get the value
     * @memberOf VariantSelectorEngine
     *
     * @example
     * // Some examples for different types of elements that could be
     * // used to select options
     *
     * // A simple button that sets a single value
     * variant_selector.addTrigger($('product__button-green'), 'click', 'color', 'green');
     *
     * // A set of radio buttons for a single option
     * // (for example a set of color swatches)
     * $('product__color-swatch').each((index, element) =>
     *     variant_selector.addTrigger($(element), 'change', 'color', element.value)
     * );
     *
     * // An example where all selectors are selected and the option is determined procedurally
     * $('product__selector').each((index, element) =>
     *     variant_selector.addTrigger($(element), 'change', element.getAttribute('name'), element.value)
     * );
     *
     * // An example where instead of setting the value, a function to retrieve the value is provided.
     * // Useful for cases like dropdown boxes.
     * const $color_dropdown = $('product__color-dropdown');
     * variant_selector.addTrigger($color_dropdown, 'change', 'color', () => $color_dropdown.val());
     */
    addTrigger($element, event, name, value) {
      // Attach the event
      $element.on(event, () => {
        let select_value;

        // Determine if we have the value or must call the function
        if (typeof value === "function") {
          select_value = value($element);
        } else {
          select_value = value;
        }

        this.updateSelection(name, select_value);
      });
    }

    /**
     * Update the variant selector with a new value for an option and
     * trigger events
     *
     * @param {string} option_name - The name of the option being updated
     * @param {string} option_value - The new value of the updated option
     * @fires VariantSelectorEngine#selection_update
     */
    updateSelection(option_name, option_value) {
      this.variant_options[option_name].selected = option_value;

      if (this.reset_selection) {
        let start_resetting = false;
        this.option_order.forEach((option) => {
          if (option === option_name) {
            start_resetting = true;
          } else if (start_resetting) {
            this.variant_options[option].selected = undefined;
          }
        });
      }

      this.updateVariantSelector();
    }

    /**
     * Directly set the variant selected by ID
     *
     * @param {number} variant_id - Variant Id to select
     * @example
     * // Example setting variants based on events from radio buttons
     * $variant_radios.on('change', event =>
     *     variant_selector.setVariant(parseInt(event.target.value))
     * );
     */
    setVariant(variant_id) {
      let variant = this.product.variants.find(
        (variant) => variant.id === variant_id
      );

      this.product.options_with_values.forEach(
        (value, index) =>
          (this.variant_options[value.name].selected = variant.options[index])
      );

      this.updateVariantSelector();
    }

    /**
     * Find the possible variants meeting the selected options and
     * call methods to update the variant selector appropriately
     * @fires VariantSelectorEngine#variant-selector__selection_update
     */
    updateVariantSelector() {
      // Find the variant that matches the currently selected set of options
      const possible_variants = this.product.variants.filter((variant) =>
        Object.keys(this.variant_options).every(
          (currentValue) =>
            // If there is no value selected for an option, allow all variants with the option
            this.variant_options[currentValue].selected === undefined ||
            variant[this.variant_options[currentValue].option_index] ===
              this.variant_options[currentValue].selected
        )
      );

      // Check that all options have something selected
      const selection_complete = Object.keys(this.variant_options).every(
        (option) => !!this.variant_options[option].selected
      );

      // Filter down variants so each option value can get a list of elements associated to it
      let filter_down_variants = this.product.variants || [];
      Object.keys(this.variant_options).forEach((option) => {
        Object.keys(this.variant_options[option].values).forEach(
          (value_key) => {
            var value = this.variant_options[option].values[value_key];

            if (filter_down_variants.length !== 0) {
              value.associated_variants = filter_down_variants.filter(
                (variant) =>
                  variant[this.variant_options[option].option_index] ===
                  value.value
              );
            } else {
              value.associated_variants = [];
            }
          }
        );

        // Before moving to the next option, filter down variants by the currently selected value for the option
        filter_down_variants = filter_down_variants.filter(
          (variant) =>
            variant[this.variant_options[option].option_index] ===
            this.variant_options[option].selected
        );
      });

      // Notify all listeners of the selection update
      /**
       * Selection update, a trigger attempted to
       * change the selected options
       *
       * @event VariantSelectorEngine#variantSelector:variant-selector__selection_update
       * @property {Object[]} possible_variants - A list of possibly selected variants. This could be 0, 1, or many variants.
       * @property {boolean} selection_complete - Whether all options were selected
       *
       * See the selection_complete, selection_illegal, and selection_incomplete events for more information on these cases.
       * @example
       * // Do something when the variant selector is updated. See VariantSelectorEngine#selection_update
       * // for why the returned variants can be a variable number (not just 1).
       * $variantSelector.on('variant-selector__selection-update', (event, variants) =>
       *     console.log('Possible number of variants that can be selected:', variants.length)
       * );
       */
      this.$event_root.trigger("variant-selector__selection-update", [
        possible_variants,
        selection_complete,
      ]);

      if (possible_variants.length === 0) {
        this.selectionIllegal();
      } else if (possible_variants.length === 1 && selection_complete) {
        this.selectionComplete(possible_variants[0]);
      } else {
        this.selectionIncomplete(possible_variants);
      }
    }

    /**
     * Perform events when one valid variant was detected
     *
     * @param {Object} variant - Selected variant
     * @fires VariantSelectorEngine#variant-selector__selection_complete
     */
    selectionComplete(variant) {
      /**
       * A selection was triggered and one variant was selected.
       * The selected options correspond to a single variant.
       * The "Happy Path" scenario for variant selection.
       *
       * @event VariantSelectorEngine#variantSelector:variant-selector__selection_complete
       * @property {Object} variant - The selected variant
       * @example
       * // Do something when a single variant has been selected
       * $variantSelector.on('variant-selector__selection-complete', (event, variant) =>
       *     $atcButton.prop('disabled', !variant.available)
       * );
       *
       * // Do something when not all options have been selected
       * $variantSelector.on('variant-selector__selection-complete', (event, variant) =>
       *     // Remind the user to select a color if none were selected
       *     if (variant_selector.variant_options['color'].selected) {
       *         $product_message.empty();
       *     } else {
       *         $product_message.html('Please select a color');
       *     }
       * );
       */
      this.$event_root.trigger("variant-selector__selection-complete", variant);
    }

    /**
     * Perform events when no variant was found
     *
     * @fires VariantSelectorEngine#variant-selector__selection_illegal
     */
    selectionIllegal() {
      /**
       * A selection was triggered and no variants could be selected.
       * This could be a case where, for example, the selected color is
       * 'green' and size is 'small,' but no variant exists that is green
       * and small.
       *
       * @event VariantSelectorEngine#variantSelector:variant-selector__selection_illegal
       */
      this.$event_root.trigger("variant-selector__selection-illegal");
    }

    /**
     * Peform events when multiple variants meet the selection criteria
     *
     * @param {Object[]} possible_variants - List of valid variants for the current selection
     * @fires VariantSelectorEngine#variant-selector__selection_incomplete
     */
    selectionIncomplete(possible_variants) {
      /**
       * A selection was triggered and multiple variants meet the selection criteria.
       * This would occur in scenarios where, for example, color is 'green' but no size
       * is selected (ie it is undefined). Therefore all variants that are 'green' will
       * be returned.
       *
       * @event VariantSelectorEngine#variantSelector:variant-selector__selection_incomplete
       * @property {Object[]} possible_variants - A list of possibly selected variants
       */
      this.$event_root.trigger(
        "variant-selector__selection-incomplete",
        possible_variants
      );
    }
  }

  /**
   * A class to hold a product's option and associated values
   */
  class VariantOption {
    /**
     * @constructs VariantOption
     * @param {Object} option - The option encapsulated by this class
     * @param {string} option.name - The name of the option
     * @param {string} option.position - The 1 indexed position of the option
     * @param {string[]} option.values - The list of values for this option
     * @param {jQuery} $event_root - The trigger origin for events
     */
    constructor(option, $event_root) {
      this.name = option.name;
      this.option_index = "option" + option.position;
      this.$event_root = $event_root;

      this.values = option.values.reduce((acc, cur) => {
        acc[cur] = new VariantOptionElement(cur, this.name, this.$event_root);
        return acc;
      }, {});

      // Define event names
      this.event_names = {
        option_updated: "variant-selector__option-updated--" + this.name,
      };
    }

    /**
     * @returns {string} The selected value for this option, can be undefined for no selection
     */
    get selected() {
      if (this._selected) {
        return this._selected.value;
      } else {
        return undefined;
      }
    }

    /**
     * Set the selected value for this option, pass a falsy value to
     * unselect the value for this option
     *
     * @param {string} value - Selected value
     * @fires VariantOption#variantSelector:variant-selector__option-updated
     */
    set selected(value) {
      if (this._selected) {
        this._selected.deselect();
      }

      if (value) {
        this._selected = this.values[value];
        this._selected.select();
      } else {
        this._selected = undefined;
      }

      /**
       * A value has been selected for this option.
       * The name of the event is 'variant-selector__option-updated--OPTION_NAME', where
       * 'OPTION_NAME' is the option to listen to.
       * @event VariantOption#variantSelector:variant-selector__option-updated
       * @property {string} value - The newly selected value
       * @example
       * // Perform an update when a specific color option is updated
       * $variantSelector.on('variant-selector__option-updated--color', (event, value) => $color_message.html('Selected Color: ' + value));
       */
      this.$event_root.trigger(this.event_names.option_updated, value);
    }
  }

  /**
   * A class to hold a value for an option
   */
  class VariantOptionElement {
    /**
     * @constructs VariantOptionElement
     * @param {string} value - Value name of this value
     * @param {jQuery} $event_root - Origin element for triggering events
     */
    constructor(value, option_name, $event_root) {
      this.value = value;
      this.$event_root = $event_root;

      this._associated_variants = [];
      this.activated = false;

      let event_suffix = `--${option_name}--${value}`;

      this.event_names = {
        selected: "variant-selector__selected" + event_suffix,
        deselect: "variant-selector__deselect" + event_suffix,
        activated: "variant-selector__activated" + event_suffix,
        deactivated: "variant-selector__deactivated" + event_suffix,
        variants_updated: "variant-selector__variants-updated" + event_suffix,
      };
    }

    /**
     * Associate a list of variants for this value of an option
     * If the list is empty then deactivate the value
     *
     * @param {Variant[]} variants - A list of possibly valid variants
     * @fires VariantOptionElement#variant-selector__variants-updated
     */
    set associated_variants(variants) {
      if (!variants || variants.length === 0) {
        this.deactivate();
      } else {
        this.activate();
      }

      this._associated_variants = variants;

      /**
       * A list of variants has been associated to this value.
       * The name of the event is 'variant-selector__variants-updated--OPTION_NAME--OPTION_VALUE', where
       * 'OPTION_NAME' is the option's name and 'OPTION_VALUE' is the respective value to listen to.
       * @event VariantOptionElement#variantSelector:variant-selector__variants-updated
       * @property {Variant[]} variants - Variants that could be selected
       * @example
       * // An example where we use variantsUpdated to set stock values for option selectors
       * // to for example show them in a tooltip
       * $('product__selector-size').each((index, element) => {
       *     let $element = $(element);
       *     $variantSelector.on('variant-selector__variants-updated--size--' + element.value, (event, variants) => {
       *         // Variants.length should be 0 or 1 since in this example 'size'
       *         // is the last option
       *         if (variants.length > 0) {
       *             $element.data('stock', variants[0].inventory_quantity);
       *         }
       *     });
       * });
       */
      this.$event_root.trigger(this.event_names.variants_updated, variants);
    }

    get associated_variants() {
      return this._associated_variants;
    }

    /**
     * Notify listeners that this value is no longer selected
     * @fires VariantOptionElement#variant-selector__deselect
     */
    deselect() {
      /**
       * This value has been deselected.
       * The name of the event is 'variant-selector__deselect--OPTION_NAME--OPTION_VALUE', where
       * 'OPTION_NAME' is the option's name and 'OPTION_VALUE' is the respective value to listen to.
       * @event VariantOptionElement#variantSelector:variant-selector__deselect
       */
      this.$event_root.trigger(this.event_names.deselect);
    }

    /**
     * Notify listeners that this value has been selected
     * @fires VariantOptionElement#selected
     */
    select() {
      /**
       * This value has been selected
       * The name of the event is 'variant-selector__selected--OPTION_NAME--OPTION_VALUE', where
       * 'OPTION_NAME' is the option's name and 'OPTION_VALUE' is the respective value to listen to.
       *
       * @event VariantOptionElement#variantSelector:variant-selector__selected
       * @example
       * // Show and hide an element when green selected
       * $variantSelector.on('variant-selector__selected--color--green', () =>
       *     $green_badge.show()
       * );
       * $variantSelector.on('variant-selector__deselect--color--green', () =>
       *     $green_badge.hide()
       * );
       */
      this.$event_root.trigger(this.event_names.selected);
    }

    /**
     * Notify listeners that this value has been deactivated
     * since there are no possible variants that could be selected
     * @fires VariantOptionElement#variant-selector__deactivated
     */
    deactivate() {
      this.activated = false;
      /**
       * This value cannot be used to select variants because
       * none exist.
       * The name of the event is 'variant-selector__deactivated--OPTION_NAME--OPTION_VALUE', where
       * 'OPTION_NAME' is the option's name and 'OPTION_VALUE' is the respective value to listen to.
       *
       * @event VariantOptionElement#variantSelector:variant-selector__deactivated
       */
      this.$event_root.trigger(this.event_names.deactivated);
    }

    /**
     * Notify listeners that this value has been activated
     * and variants can be selected for
     * @fires VariantOptionElement#variant-selector__activated
     */
    activate() {
      this.activated = true;
      /**
       * This value can be used to select variants.
       * The name of the event is 'variant-selector__activated--OPTION_NAME--OPTION_VALUE', where
       * 'OPTION_NAME' is the option's name and 'OPTION_VALUE' is the respective value to listen to.
       *
       * @event VariantOptionElement#variantSelector:variant-selector__activated
       * @example
       * // An example where we procedurally set selectors to be enabled/disabled
       * $('product__selector').each((index, element) => {
       *     let $element = $(element);
       *     $variantSelector.on(`variant-selector__activated--${element.getAttribute('name')}--${element.value}` () =>
       *         $element.removeClass('disabled')
       *     );
       *     $variantSelector.on(`variant-selector__deactivated--${element.getAttribute('name')}--${element.value}`, () =>
       *         $element.addClass('disabled')
       *     );
       * });
       */
      this.$event_root.trigger(this.event_names.activated);
    }
  }

  theme.VariantSelector = VariantSelector;

  // Initialize variant selectors on the page.
  $(() => {
    theme.product_vs = $(".js-product-container")
      .get()
      .reduce((acc, element) => {
        let $element = $(element);
        acc[$element.data("product-handle")] = new VariantSelector($element, {
          update_atc: false,
          force_id_update: true, // Force the id update so that the selected color can be picked up by Gift Reggie
        });

        return acc;
      }, {});
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * ATC State Manager ALPHA from Foxpad
 */

((theme, $) => {
  class LinearState {
    observer() {
      console.warm("not implemented");
      return false;
    }
    action() {
      console.warm("not implemented");
    }
  }

  /**
   * Features:
   * - Event consolidation: Even if multiple states listen to the same state
   *   multiple times, the update flow only occurs once
   */
  class LinearStateManager {
    constructor() {
      this._state_queue = [];
      this._listeners = {};
    }

    addTriggerListener(event_key, listener) {
      if (this._listeners[event_key]) {
        this._listeners[event_key].push(listener);
      } else {
        this._listeners[event_key] = [listener];
      }
    }

    triggerEventUpdate(event_key, event_data) {
      let listeners = this._listeners[event_key];
      if (listeners) {
        listeners.forEach((listener) => listener(event_data));
        this.update();
      }
    }

    addState(state) {
      this._state_queue.push(state);
    }

    update() {
      for (let i = this._state_queue.length - 1; i >= 0; i--) {
        const execute_state = this._state_queue[i].observer();
        if (execute_state) {
          this._state_queue[i].action();
          break;
        }
      }
    }
  }

  theme.LinearStateManager = LinearStateManager;
  theme.LinearState = LinearState;
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  const EVENT_KEYS = {
    variant_selector_update: "variant_selector_update",
    cart_set: "cart_set",
  };

  class ATCState extends theme.LinearState {
    constructor({ state_text, $atc_button }) {
      super();
      this._state_text = update_text;
      this._$atc_buttom = $atc_button;
    }

    updateText(new_text) {
      if (new_text === undefined) {
        this._$atc_buttom.text(this._state_text);
      } else {
        this._$atc_buttom.text(new_text);
      }
    }
  }

  class DefaultState extends theme.LinearState {
    constructor(state_text, update_state) {
      super();
      this._state_text = state_text;
      this._update_state = update_state;
    }
    observer() {
      return true;
    }
    action() {
      this._update_state(this._state_text, false);
    }
  }

  /**
   * @class
   * @namespace CartInventoryCheck
   */
  class CartInventoryCheck extends theme.LinearState {
    constructor(state_text, atc_state_manager, initial_cart_content) {
      super();
      this._state_text = state_text;
      this._atc_state_manager = atc_state_manager;

      this._selected_variants = undefined;
      this._cart_content = initial_cart_content;
      this._complete = false;

      this._atc_state_manager.addTriggerListener(
        EVENT_KEYS.variant_selector_update,
        ({ variants, complete }) => {
          this._selected_variants = variants;
          this._complete = complete;
        }
      );
      this._atc_state_manager.addTriggerListener(
        EVENT_KEYS.cart_set,
        (content) => (this._cart_content = content)
      );
    }

    observer() {
      if (
        this._selected_variants === undefined ||
        this._cart_content === undefined ||
        !this._complete
      ) {
        return false;
      }

      let selected_variant = this._selected_variants[0];

      if (
        selected_variant.inventory_management !== "shopify" ||
        selected_variant.inventory_policy === "continue"
      ) {
        return false;
      }

      let selected_variant_count = this._cart_content.items
        .filter((line_item) => line_item.variant_id === selected_variant.id)
        .reduce((acc, item) => acc + item.quantity, 0);

      return selected_variant_count >= selected_variant.inventory_quantity;
    }
    action() {
      this._atc_state_manager.updateATC(this._state_text, true);
    }
  }

  /**
   * TODO:
   * - Look into removing dependency on atc_state_manager
   * - Add button disabling
   * - Bubble button updating to common parent class
   */
  class VariantSelectionCheck extends theme.LinearState {
    constructor(state_text, atc_state_manager) {
      super();

      this._state_text = state_text;
      this._atc_state_manager = atc_state_manager;

      this._selected_variants = undefined;
      this._complete = false;

      this._atc_state_manager.addTriggerListener(
        EVENT_KEYS.variant_selector_update,
        ({ variants, complete }) => {
          this._selected_variants = variants;
          this._complete = complete;
        }
      );
    }

    observer() {
      if (this._selected_variants === undefined) {
        return false;
      }

      return this._selected_variants.length > 1 || !this._complete;
    }

    action() {
      this._atc_state_manager.updateATC(this._state_text, false, true);
    }
  }

  class VariantInventoryCheck extends theme.LinearState {
    constructor(state_text, atc_state_manager) {
      super();

      this._state_text = state_text;
      this._atc_state_manager = atc_state_manager;

      this._selected_variants = undefined;
      this._complete = false;

      this._atc_state_manager.addTriggerListener(
        EVENT_KEYS.variant_selector_update,
        ({ variants, complete }) => {
          this._selected_variants = variants;
          this._complete = complete;
        }
      );
    }

    observer() {
      if (this._selected_variants === undefined || !this._complete) {
        return false;
      }

      let selected_variant = this._selected_variants[0];

      if (
        selected_variant.inventory_management !== "shopify" ||
        selected_variant.inventory_policy === "continue"
      ) {
        return false;
      }

      return selected_variant.inventory_quantity === 0;
    }

    action() {
      this._atc_state_manager.updateATC(this._state_text, true);
    }
  }

  class ATCStateManager extends theme.LinearStateManager {
    constructor($atc_button, options) {
      super();

      this.$atc_button = $atc_button;

      const defaults = {
        variant_selector: {
          event_root: $atc_button.closest("form"),
          hook_update: true,
          hook_incomplete: true,
          strings: {
            out_of_stock: "Out of stock",
            incomplete: "Select all options",
          },
        },
        cart: {
          cart: theme.cart,
          cart_event: "Cart:set",
          hook_update: true,
          strings: {
            in_cart: "Max quantity already in cart",
          },
        },
        strings: {
          atc: "Add to cart",
        },
      };

      const data_options = this.$atc_button.data("variant-selector");

      this._options = $.extend(true, {}, defaults, data_options, options);

      this._generateStates();
      this._generateTriggers();
    }

    _generateStates() {
      const default_state = new DefaultState(
        this._options.strings.atc,
        this.updateATC.bind(this)
      );
      const variant_selection_check = new VariantSelectionCheck(
        this._options.variant_selector.strings.incomplete,
        this
      );
      const variant_inventory_check = new VariantInventoryCheck(
        this._options.variant_selector.strings.out_of_stock,
        this
      );
      const cart_inventory_check = new CartInventoryCheck(
        this._options.cart.strings.in_cart,
        this,
        theme.cart && theme.cart.content
      );

      this.addState(default_state);
      this.addState(variant_selection_check);
      this.addState(cart_inventory_check);
      this.addState(variant_inventory_check);
    }

    _generateTriggers() {
      $(this._options.variant_selector.event_root).on(
        "variant-selector__selection-update.ATC_State_Manager",
        (event, variants, complete) => {
          this.triggerEventUpdate(EVENT_KEYS.variant_selector_update, {
            variants: variants,
            complete: complete,
          });
        }
      );

      $(document).on(
        this._options.cart.cart_event + ".ATC_State_Manager",
        (event, content) => {
          this.triggerEventUpdate(EVENT_KEYS.cart_set, content);
        }
      );
    }

    updateATC(text, disabled = true, visuallyDisabled = false) {
      this.$atc_button
        .text(text)
        .prop("disabled", disabled)
        .toggleClass("disabled", visuallyDisabled);

      // Sticky ATC
      document.querySelector("#stickyATC button").innerHTML = text;
    }
  }

  theme.ATCStateManager = ATCStateManager;
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  $(document).on("Addresses:update-valid", ".js-customer-address", (e) => {
    new theme.Modal($(".js-modal", $(e.target)));
  });
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  const HEADER_OPEN_STATE = "is-open";
  const HEADER_TRANSITION_TO_FIXED_STATE = "is-transitioning-to-fixed";
  const HEADER_FIXED_STATE = "is-fixed";

  /**
   * The HeaderMenuManager handles a set of menu items and controls their jQuery slide behaviour.
   * Options are responsive and behaviours can be customized for desktop and mobile.
   * @class
   * @property {object} options - All Header Menu options
   * @property {object} settings - The options that are unique to the current screen size
   */
  class HeaderMenuManager {
    /**
     * @constructs HeaderMenuManager
     * @param {jQuery} $containers - A single jQuery object with all the menu items to manage
     * @param {object} [options] - Manager options
     * @param {object} [options.breakpoint=799] - The breakpoint at which to switch to desktop options
     * @param {object} [options.desktop] - Settings to apply on desktop
     * @param {boolean} [options.desktop.enabled=true] - Enable or disable the sliding behaviour on desktop
     * @param {boolean} [options.desktop.default_open=false] - Whether the menu should be slide down by default, if this is enabled you might what to disable collapse_others
     * @param {boolean} [options.desktop.collapse_others=true] - Whether the opening the menu should collapse its sibling menus
     * @param {object} [options.mobile] - Settings to apply on mobile
     * @param {boolean} [options.mobile.enabled=true] - Enable or disable the sliding behaviour on mobile
     * @param {boolean} [options.mobile.default_open=false] - Whether the menu should be slide down by default, if this is enabled you might what to disable collapse_others
     * @param {boolean} [options.mobile.collapse_others=true] - Whether the opening the menu should collapse its sibling menus
     */
    constructor($containers, options) {
      let defaults = {
        breakpoint: window.theme.bp.header,
        desktop: {
          enabled: true,
          default_open: false,
          collapse_others: true,
          fixed: true,
        },
        mobile: {
          enabled: true,
          default_open: false,
          collapse_others: true,
          fixed: true,
        },
      };

      this.options = $.extend(true, defaults, options);

      // Generate the menu items
      this.header_menus = [];
      $containers.get().map((element) => new HeaderMenu($(element), this));

      // Setup the window resize listeners
      this.$window = $(window);
      this.$window.on(
        "resize",
        $.debounce(250, () => {
          this._resize();
        })
      );
      this._resize();
    }

    /**
     * On resize, check if we've switched from desktop to mobile
     * and trigger a refresh
     * @memberof HeaderMenuManager
     * @private
     */
    _resize() {
      let desktop_enabled = this.$window.width() > this.options.breakpoint;
      if (this.desktop_enabled !== desktop_enabled) {
        this.desktop_enabled = desktop_enabled;
        this.settings = desktop_enabled
          ? this.options.desktop
          : this.options.mobile;
        this.refreshLayout();
      }
    }

    /**
     * Add a menu to be handled by this manager
     * @param {HeaderMenu} menu - A menu to register with this manager
     * @memberof HeaderMenuManager
     */
    addMenu(menu) {
      this.header_menus.push(menu);
    }

    /**
     * Close all header menus except the one provided. To just close
     * all menus don't pass a closer.
     * @param {HeaderMenu} [closer] - The header menu requesting the close
     * @memberof HeaderMenuManager
     */
    closeOthers(closer) {
      this.header_menus.forEach((menu) => {
        if (menu !== closer) {
          menu.close();
        }
      });
    }

    /**
     * Refresh the layout of the header menu
     * @memberof HeaderMenuManager
     */
    refreshLayout() {
      // Depending on the set default state,
      // open or close all the menus
      if (this.settings.default_open) {
        this.header_menus.forEach((menu) => menu.open());
      } else {
        this.header_menus.forEach((menu) => menu.close());
      }
    }
  }

  /**
   * A HeaderMenu is a single menu that can be opened and closed.
   * It is grouped with other HeaderMenu objects through a HeaderMenuManager.
   * @class
   */
  class HeaderMenu {
    /**
     * @constructs HeaderMenu
     * @param {jQuery} $menu - The menu's DOM element
     * @param {HeaderMenuManager} manager - The parent HeaderMenuManager
     */
    constructor($menu, manager, $trigger) {
      this.elements = {
        $menu: $menu,
        $menu_trigger:
          $trigger && $trigger.length
            ? $trigger
            : $menu.siblings(".js-menu-trigger"),
        $menu_close: $(".js-menu-close", $menu),
        $body: $("body"),
        $image: $(".js-mega-menu-image", $menu),
        $fixed_container: $(".js-header-fixed"),
      };

      (this.hasChildren = false), (this.opened = false);
      this.closeTimeout = null; // Variable para el timeout de cierre
      this._checkForChildren();
      this._setupManager(manager);
      this._setupEvents();

      if (this.elements.$image.length) {
        this._setupSubLinks();
      }
    }

    /**
     * Register the menu with a menu manager
     * @param {HeaderMenuManager} manager - The parent HeaderMenuManager
     * @memberof HeaderMenu
     * @private
     */
    _setupManager(manager) {
      this._manager = manager;
      this._manager.addMenu(this);
    }
    /**
     * Setup menu events
     * @memberof HeaderMenu
     * @private
     */
    _setupEvents() {
      this.elements.$menu_trigger.on("mouseenter", () => {
        if (this._manager.settings.enabled) {
          // Clear any pending close timeout
          if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
          }
          
          if (this._manager.desktop_enabled) {
            this.open();
          }
          return false;
        }
      });

      $(this.elements.$menu_trigger)
        .add(this.elements.$menu)
        .on("mouseenter", (event) => {
          // Clear any pending close timeout when mouse enters menu
          if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
          }
        })
        .on("mouseout", (event) => {
          if (this._manager.settings.enabled) {
            let $target = $(event.relatedTarget).parents().addBack();
            let is_other_menu_item =
              !$target.is(this.elements.$menu) &&
              !$target.is(this.elements.$menu_trigger);

            if (
              this._manager.desktop_enabled &&
              is_other_menu_item &&
              this.opened
            ) {
              // Clear any existing timeout
              if (this.closeTimeout) {
                clearTimeout(this.closeTimeout);
              }
              
              // Set timeout to close menu after 1 second
              this.closeTimeout = setTimeout(() => {
                this.close();
                this.closeTimeout = null;
              }, 500);
            }
            return false;
          }
        });

      this.elements.$menu_trigger.on("click", () => {
        if (this._manager.settings.enabled) {
          if (!this._manager.desktop_enabled) {
            this.toggle();
          }
        }
        if (this.hasChildren) {
          return false;
        }
      });

      this.elements.$menu_close.on("click", () => {
        if (!this._manager.desktop_enabled) {
          this.close();
        }
        return false;
      });

      if (
        this.elements.$fixed_container &&
        this._manager.settings &&
        this._manager.settings.enabled
      ) {
        setTimeout(this.scroll(), 1000);

        $(window).on(
          "scroll",
          $.debounce(15, () => {
            this.scroll();
          })
        );

        window.addEventListener(
          "touchmove",
          $.debounce(15, () => {
            this.scroll();
          }),
          { passive: false }
        );
      }
    }

    /**
     * Setup link-image elements
     * @memberof HeaderMenu
     * @private
     */
    _setupSubLinks() {
      let manager = this;
      $(".js-sub-menu-item", manager.elements.$menu).each(function (i, link) {
        new HeaderLinkImage($(link), manager.elements.$image);
      });
    }

    _checkForChildren() {
      if (
        this.elements.$menu[0] &&
        this.elements.$menu[0].classList.contains("js-search-menu")
      )
        return;

      const menuChildren = this.elements.$menu_trigger
        .siblings(".js-mega-menu")
        .find(".js-mega-nav-item");
      if (menuChildren.length) {
        this.hasChildren = true;
      } else {
        this.elements.$menu_trigger.addClass("c-nav__linklist-single-link");
        this.elements.$menu.remove();
      }
    }

    /**
     * Close this menu
     * @memberof HeaderMenu
     */
    close() {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      
      this.elements.$menu_trigger.removeClass(HEADER_OPEN_STATE);
      this.elements.$menu.slideUp();
      this.opened = false;
    }

    /**
     * Open this menu, close other menus if
     * the setting is enabled
     * @memberof HeaderMenu
     */
    open() {
      if (this._manager.settings.collapse_others) {
        this._manager.closeOthers(this);
      }

      this.elements.$menu_trigger.addClass(HEADER_OPEN_STATE);
      this.elements.$menu.slideDown(() => {
        this.opened = true;
      });
    }

    scroll() {
      if (this._manager.settings.fixed) {
        let documentPositionFromTop = document.documentElement.scrollTop;

        if (navigator.userAgent.match(/(iPod|iPhone|iPad|Android)/)) {
          documentPositionFromTop = window.pageYOffset;
        }

        const $fixedContainerHeight =
          this.elements.$fixed_container.height() + 25;
        const $thresholdHeight = $fixedContainerHeight + 100;

        if (
          documentPositionFromTop > $fixedContainerHeight &&
          documentPositionFromTop < $thresholdHeight
        ) {
          this.elements.$fixed_container.addClass(
            HEADER_TRANSITION_TO_FIXED_STATE
          );
        } else if (documentPositionFromTop > $thresholdHeight) {
          this.elements.$fixed_container.addClass(
            HEADER_TRANSITION_TO_FIXED_STATE
          );
          this.elements.$fixed_container.addClass(HEADER_FIXED_STATE);
        } else if (
          documentPositionFromTop < $thresholdHeight &&
          documentPositionFromTop > $fixedContainerHeight
        ) {
          this.elements.$fixed_container.removeClass(HEADER_FIXED_STATE);
        } else if (documentPositionFromTop < $fixedContainerHeight) {
          this.elements.$fixed_container.removeClass(
            HEADER_TRANSITION_TO_FIXED_STATE
          );
          this.elements.$fixed_container.removeClass(HEADER_FIXED_STATE);

          // if cart flyout is open on scroll start, close it
          // if (
          //   document
          //     .querySelector(".js-cart-flyout")
          //     .classList.contains("flyout-active-right")
          // ) {
          //   document.querySelector(".js-header-flyout").click();
          // }
        }
      }
    }

    /**
     * Toggle the menu
     * @memberof HeaderMenu
     */
    toggle() {
      if (this.elements.$menu_trigger.hasClass(HEADER_OPEN_STATE)) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  /**
   * Extend the standard header menu to
   * provide search specific behaviours
   * @class
   */
  class HeaderSearchMenu extends HeaderMenu {
    /**
     * @param {HeaderMenuManager} manager - The menu manager to attach to
     */
    constructor(manager) {
      super($(".js-search-menu"), manager, $(".js-search-menu-trigger"));

      this.$input_desktop = $(".js-search-input-desktop", this.elements.$menu);
      this.$input_mobile = $(".js-search-input-mobile", this.elements.$menu);
    }

    /**
     * Extend the open function to also focus the
     * appropriate input element
     * @override
     * @memberof HeaderSearchMenu
     */
    open() {
      super.open();

      if (this._manager.desktop_enabled) {
        this.$input_desktop.focus();
      } else {
        this.$input_mobile.focus();
      }
    }
  }
  /**
   * The HeaderLinkImage handles preloading images to be displayed in the mega menu
   * when hovering on a link of said menu to Preload images and avoid load lag on hover
   * @class
   */
  class HeaderLinkImage {
    /**
     * @constructs HeaderLinkImage
     * @param {jQuery} $link - The link's DOM element
     * @param {jQuery} menu_image - The DOM element of the image being updated
     */
    constructor($link, $menu_image) {
      this.elements = {
        $link: $link,
        $menu_image: $menu_image,
      };

      // Create the image is there is a src for it
      if ($link.data("image").length) {
        this.elements.image = new Image();
        this.elements.image.src = $link.data("image");
      }

      this._setupEvents();
    }

    /**
     * Setup link-image events
     * @memberof HeaderLinkImage
     * @private
     */
    _setupEvents() {
      // Update mega menu image when a menu item is hovered if it has an image,
      // hide otherwise to avoid showing the broken image icon
      this.elements.$link.on("mouseenter", (e) => {
        if (this.elements.image) {
          this.elements.$menu_image.attr("src", this.elements.image.src);
          this.elements.$menu_image.addClass("image--loaded");
        } else {
          this.elements.$menu_image.attr("src", "");
          this.elements.$menu_image.removeClass("image--loaded");
        }
      });
    }
  }

  // Construct the header's main mega menu manager
  theme.mega_menu_manager = new HeaderMenuManager($(".js-mega-menu"));

  // Attach the search menu to the mega menu manager
  new HeaderSearchMenu(theme.mega_menu_manager);

  // Construct the sub menu's menu manager
  theme.sub_menu_manager = new HeaderMenuManager($(".js-sub-menu"), {
    desktop: {
      enabled: false,
      default_open: true,
      collapse_others: false,
    },
  });

  // Add clear button listeners to clear the search input box
  (() => {
    const $search_header = $(".js-header-search");
    const $search_inputs = $(".js-search-input", $search_header);
    const $search_clear = $(".js-search-clear", $search_header);

    $search_clear.on("click", () => {
      $search_inputs.val("");
    });
  })();

  // Add event listeners to the header flyout to update the hamburger menu button
  (() => {
    const $header_flyout = $(".js-header-flyout");
    const $header_hamburger = $(".js-header-flyout-hamburger");
    const $header_lower = $(".c-header__lower");

    $header_flyout
      .on("Flyout:opening", () => {
        $header_lower.addClass(HEADER_OPEN_STATE);
        $header_hamburger.addClass(HEADER_OPEN_STATE);
      })
      .on("Flyout:closing", () => {
        $header_lower.removeClass(HEADER_OPEN_STATE);
        $header_hamburger.removeClass(HEADER_OPEN_STATE);
      });
  })();

  $(".searchTrigger").on("click", function () {
    var $me = $(this);
    if (!$me.hasClass("Active")) {
      $me.addClass("Active");
      $(".jssearch-menu").addClass("show");
      $(".c-header-search__input").focus();
      setTimeout(function () {
        $(".c-header-search__form").addClass("show");
      }, 100);
    } else {
      $(".c-header-search__form").removeClass("show");
      setTimeout(function () {
        $me.removeClass("Active");
        $(".jssearch-menu").removeClass("show");
        $(".c-header-search__input").blur();
      }, 100);
    }
  });

  $(".countryOption").on("click", function () {
    var $me = $(this),
      meVal = $me.attr("data-value"),
      me_select = $me.attr("data-select");
    $(me_select).val(meVal).trigger("change");
  });

  $("body .desktop-header").on("click", function (e) {
    var $target = $(e.target);
    if ($target.hasClass("countryTrigger")) {
      var $me = $target,
        me_country_menu = $me.attr("data-country-selector");
      $me.toggleClass("Active");
      $("." + me_country_menu).toggle();
    } else if ($target.hasClass("countrySelector") == false) {
      $(".countryTrigger").removeClass("Active");
      $(".countrySelector").hide();
    }
  });
  $("body .c-header").on("click", function (e) {
    var $target = $(e.target);
    if ($target.hasClass("countryTrigger")) {
      var $me = $target,
        me_country_menu = $me.attr("data-country-selector");
      $me.toggleClass("Active");
      $("." + me_country_menu).toggle();
    } else if ($target.hasClass("countrySelector") == false) {
      $(".countryTrigger").removeClass("Active");
      $(".countrySelector").hide();
    }
  });
})((window.theme = window.theme || {}), jQuery);

$(() => {
  var $newsletterModal = $("#newsletter-thank-you.js-modal"),
    newsletterModal = $newsletterModal.length
      ? new theme.Modal($newsletterModal)
      : null;

  // send an AJAX Post request to the mailchimp server, adding this subscriber to the main list. We'll get back a JSONP response with the callback function that we specified in the 'c' parameter.
  $(document).on("submit", ".js-newsletter-form", function (e) {
    e.preventDefault();
    var $form = $(this);
    var newsLetterSignupURL = $form.attr("action");
    newsLetterSignupURL = newsLetterSignupURL.replace(
      "/post?u=",
      "/post-json?u="
    );
    newsLetterSignupURL = newsLetterSignupURL + "&c=displayMailChimpResponse";

    $.post(newsLetterSignupURL, $form.serialize(), null, "jsonp");
  });

  // This is the JSONP callback function for the Mailchimp Ajax request. Must be defined globally.
  window.displayMailChimpResponse = function (resp) {
    if (resp.result == "success") {
      newsletterModal && newsletterModal.open();
      $(".js-newsletter-form-inputs").hide();
    }
    $(".js-newsletter-response")
      .html("<p>" + resp.msg + "</p>")
      .show();
  };
});

((theme, $) => {
  $(".js-video-toggle").on("click", (e) => {
    let $this = $(e.currentTarget);
    let $container = $this.closest(".js-video");
    let video = $("video", $container).get(0);
    let $button = $(".js-play-icon", $container);

    video.play();
    $container.addClass("is-playing");
  });

  $(document).on("Modal:close", function (e, $modal_wrapper) {
    let $container = $($modal_wrapper.closest(".js-video"));
    let video = $("video", $container).get(0);

    if ($container.hasClass("is-playing")) {
      video.pause();
      $container.removeClass("is-playing");
    }
  });
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  class Tooltip {
    /**
     * @constructs Tooltip
     * @param {jQuery} $tooltip - The tooltip's DOM element
     */
    constructor($tooltip) {
      this.elements = {
        $tooltip: $tooltip,
        $open: $(".js-tooltip-open", $tooltip),
        $close: $(".js-tooltip-close", $tooltip),
      };

      this._setupEvents();
    }

    /**
     * Setup tooltip events
     * @memberof Tooltip
     * @private
     */
    _setupEvents() {
      this.elements.$open.on("mouseenter", (e) => {
        if (!this.elements.$tooltip.hasClass("o-tooltip--is-open")) {
          this.elements.$tooltip.addClass("o-tooltip--is-open");
        }
      });

      this.elements.$close.on("click", (e) => {
        if (this.elements.$tooltip.hasClass("o-tooltip--is-open")) {
          this.elements.$tooltip.removeClass("o-tooltip--is-open");
        }
      });
    }
  }

  (() => {
    $(".js-tooltip").each(function (i, tooltip) {
      new Tooltip($(tooltip));
    });
  })();
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  class Filters {
    constructor(form) {
      this.api = new FESA();
      this.form = form;
      this.collection = "all";
      this.showCount = true;
      this.index = 0;
      this.allVariants = true;
      this.pageSize = 100;
      this.currentPage = 1;
      this.fields = [
        "id",
        "handle",
        "title",
        "images",
        "tags",
        "options",
        "variants",
        "metafields",
      ];
      this.options = [];
      this.optionSets = {};
      this.defaultSort = "priority";
      this.productCount = document.querySelector(".product-count").innerHTML;
      this.collectionBody = document.querySelector(
        "#collection_body .js-filter-products"
      ).innerHTML;
      this.customSorting = {
        Size: this.sortSize,
      };

      let productContainer = document.querySelector(".js-filter-products");
      let activeFilters = this.form.querySelector("[data-filters-active]");

      this.form.addEventListener("change", (e) => {
        if (activeFilters && !$(e.target).is('[name="sort-order"]')) {
          this.updateActiveFilters(activeFilters);
          $(".active-filters").show();
        }

        if (productContainer) {
          if (
            !$(e.target).is('[name="sort-order"]') &&
            this.form.querySelectorAll("[data-filters-active] label").length ==
              0
          ) {
            $(".product-count").html(this.productCount);
            productContainer.innerHTML = this.collectionBody;
            $(".js-filter-paginate").show();
            $(".active-filters").hide();
          } else {
            this.filterProducts(productContainer);
            $(".js-filter-paginate").hide();
          }
        }
      });
    }

    query() {
      let query = this.api
        .s()
        .hostname(window.theme.shop.permanent_domain)
        .count(this.showCount)
        .collection(this.collection)
        .index(this.index)
        .allVariants(this.allVariants)
        .paginate(this.pageSize)
        .page(this.currentPage)
        .fields(this.fields)
        .options(this.options, true)
        .sort(this.getSort());
      return this.applyFacets(query).e();
    }

    getSort() {
      let selected = this.form.querySelector('[name="sort-order"]:checked');
      return selected
        ? selected.value == "price-desc"
          ? { desc: "price" }
          : selected.value
        : this.defaultSort;
    }

    applyFacets(query) {
      let queries = {};
      this.form.querySelectorAll(".filter__facet:checked").forEach((input) => {
        if (!(input.name in queries)) {
          queries[input.name] = [];
        }

        let facets = this.mapFacets(input.value, input.name);
        switch (this.optionSets[input.name].dataset.filterType) {
          case "option":
            facets.forEach((facet) =>
              queries[input.name].push(this.api.s().option(input.name, facet))
            );
            break;
          case "tags":
            facets.forEach((facet) =>
              queries[input.name].push(this.api.s().tag(facet))
            );
            break;
          default:
            facets.forEach((facet) =>
              queries[input.name].push(this.api.s()[input.name](facet))
            );
            break;
        }
      });
      for (let key in queries) {
        query.or.apply(query, queries[key]);
      }
      return query;
    }

    updateActiveFilters(container) {
      let activeFilters = [];
      this.form.querySelectorAll(".filter__facet:checked").forEach((input) => {
        let label = $(input).siblings(".filter__label").get(0);
        if (label) {
          activeFilters.push(label.outerHTML);
        }
      });

      container.parentNode.classList[activeFilters.length ? "remove" : "add"](
        "no-filters"
      );
      container.innerHTML = activeFilters.join("");
    }

    renderOptions() {
      this.form
        .querySelectorAll("[data-filter-option]")
        .forEach((optionSet) => {
          this.options.push(
            this.getOptionKey(
              optionSet.dataset.filterType,
              optionSet.dataset.filterOption
            )
          );
          this.optionSets[optionSet.dataset.filterOption] = optionSet;
        });

      if (!this.options.length) {
        return;
      }

      // START Tag facet workaround
      this.options = this.options.filter((option) => !("tags" in option));
      // END Tag facet workaround
      this.query().done((products, count, response) => {
        for (let option of response.options) {
          option = "option" in option ? option.option : option; // TODO implement tags

          for (let key in option) {
            for (let facet of this.sortFacets(
              this.filterFacets(option[key], key),
              key
            )) {
              this.optionSets[key].innerHTML += this.renderOption(
                key,
                facet.value,
                facet.count
              );
            }
          }
        }
        // START Tag facet workaround
        let facet = "subsubcategory::";
        for (let value of window.theme.all_tags.filter(
          (tag) => tag.indexOf(facet) === 0
        )) {
          this.optionSets[facet].innerHTML += this.renderOption(
            facet,
            value,
            1
          );
        }
        // END Tag facet workaround
      });
    }

    renderOption(facet, value, count) {
      let filterId = `filter-${facet}-${value}`,
        filterLabel = value;
      if (facet == "Colour") {
        filterLabel = `
					<span class="filter__title">${filterLabel}</span>
					<span class="filter__swatch o-swatch color-filter--${Shopify.handleize(
            value
          )}"></span>
					`;
      } else if (value.indexOf(facet) === 0) {
        filterLabel = value.replace(facet, "");
      }
      return `
				<div class="filter">
					<input type="checkbox" class="filter__facet" id="${filterId}" name="${facet}" value="${value}">
					<label class="filter__label" for="${filterId}" data-filter-count="${count}">${filterLabel}</label>
				</div>
			`;
    }

    filterFacets(facets, type) {
      if (type != "Colour") {
        return facets;
      }

      let lowercaseFacets = {};

      $.each(facets, function (key, value) {
        let lowercaseKey = key.toLowerCase();
        lowercaseFacets[lowercaseKey] = value;
      });

      let colorFacets = {};
      for (let colorLabel in theme.color_filters) {
        let count = theme.color_filters[colorLabel].reduce((sum, option) => {
          sum += option in lowercaseFacets ? lowercaseFacets[option] : 0;
          return sum;
        }, 0);
        if (count > 0) {
          colorFacets[colorLabel] = count;
        }
      }
      return colorFacets;
    }

    mapFacets(value, type) {
      if (type != "Colour") {
        return [value];
      }
      return theme.color_filters[value];
    }

    sortFacets(facets, type) {
      let sortedFacets = [],
        facetKeys = Object.keys(facets);
      if (type != "Colour") {
        type in this.customSorting
          ? facetKeys.sort(this.customSorting[type])
          : facetKeys.sort();
      }
      for (let key of facetKeys) {
        sortedFacets.push({ value: key, count: facets[key] });
      }
      return sortedFacets;
    }

    sortSize(first, second) {
      let sizeOrder = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
      let firstNum = parseFloat(first),
        secondNum = parseFloat(second);
      if (!isNaN(firstNum) && isNaN(secondNum)) return -1;
      if (isNaN(firstNum) && !isNaN(secondNum)) return 1;
      if (!isNaN(firstNum) && !isNaN(secondNum)) return firstNum - secondNum;
      return sizeOrder.indexOf(first) - sizeOrder.indexOf(second);
    }

    getOptionKey(type, key) {
      switch (type) {
        case "option":
          key = { option: key };
          break;
        case "tags":
          key = {
            tags: window.theme.all_tags.filter((tag) => tag.indexOf(key) === 0),
          };
          break;
        default:
          key = key;
          break;
      }
      return key;
    }

    filterProducts(container) {
      $("#collection_body").add(".loading-overlay").addClass("loading");
      this.query().done((products, count, response) => {
        let filterSizes = [];
        this.form
          .querySelectorAll('[data-filters-active] [for*="filter-Size"]')
          .forEach((item) => {
            filterSizes.push(item.textContent.trim());
          });

        let html = [];
        for (const product of products) {
          if (filterSizes.length != 0) {
            product.variants.map((variant) => {
              if (
                (filterSizes.includes(variant.option2) &&
                  variant.inventory_quantity > 0) ||
                variant.inventory_policy == "continue"
              ) {
                html.push(this.renderProduct(product));
              }
            });
          } else {
            html.push(this.renderProduct(product));
          }

          this.renderColorProduct(product);
        }

        let unique = html.filter((item, i, ar) => ar.indexOf(item) === i);
        container.innerHTML = unique.join("");

        if (
          container.querySelectorAll(".c-collection__product-tile").length > 0
        ) {
          $(".products-empty").hide();
          $(".product-count").html(
            `${
              container.querySelectorAll(".c-collection__product-tile")
                .length == 1
                ? container.querySelectorAll(".c-collection__product-tile")
                    .length + " item"
                : container.querySelectorAll(".c-collection__product-tile")
                    .length + " items"
            }`
          );
        } else {
          $(".product-count").html("");
          $(".products-empty").show();
          setTimeout(() => {
            $("#collection_body")
              .add(".loading-overlay")
              .removeClass("loading");
          }, 300);
        }

        var api = new Yotpo.API(yotpo);
        api.refreshWidgets();
        $(".gift-reggie-wishlist-button", $(container))
          .filter(":empty")
          .each(function () {
            if (typeof window.GiftReggie !== "undefined") {
              window.GiftReggie.publishButton($(this));
            }
          });
      });
    }

    renderColorProduct(prd) {
      let product_handle = Shopify.handleize(prd.title);
      let product_group = "_product-group-" + product_handle;
      let collection = this.collection;
      let colors = [];
      let htmlColor = "";
      let limit = 4;
      let count = 0;

      $.ajax({
        type: "GET",
        url: "/collections/" + product_group + "/products.json",
        datatype: "json",
        success: function (data) {
          if (data.products.length > 0) {
            const obj = {};
            data.products.forEach((item) => {
              colors.push({
                handle: item.handle,
                tile_image: item.images[0].src,
                tile_image_hover: item.images[1] ? item.images[1].src : item.images[0].src,
                colour: item.variants[0].option1.toLowerCase(),
              });
            });
            if (colors.length && theme.template.suffix !== "no-swatch") {
              htmlColor += `
                <div class="c-collection__colors c-option__colors o-swatch-list">`;

              colors.forEach((color, index) => {
                let color_id = `prod-color-${prd.handle}-${Shopify.handleize(
                  color.colour
                )}`;
                let color_state = color.handle == prd.handle ? "checked" : "";
                let colorImage =
                  color.tile_image != "" ? color.tile_image : featured_image;

                for (const key in theme.color_filters) {
                  if (theme.color_filters[key].includes(color.colour)) {
                    if (!obj[key]) {
                      obj[key] = color.colour;
                      count = count + 1;
                      htmlColor += `
                          <input class="o-swatch-input js-color-swatch ${index}
                          ${count > limit ? "hide" : ""} " data-handle="${
                        color.handle
                      }" id="${color_id}" type="radio" 
                          name="${prd.handle}-colour" 
                          value="${
                            color.colour
                          }" data-url="/collections/${collection}/products/${
                        color.handle
                      }" 
                          data-image="${colorImage}" ${color_state}>
                          <label data-handle="${
                            color.handle
                          }" class="c-collection__option-color c-option__color o-swatch 
                          swatch-${Shopify.handleize(color.colour)}" 
                          for="${color_id}" title="${
                        color.colour
                      }">${key}</label>
                        `;
                    } else {
                      break;
                    }
                  }
                }
              });

              colors.length > limit
                ? (htmlColor += `<span class="remaining-colors"> + ${
                    colors.length - limit
                  }</span>`)
                : "";
              htmlColor += `
                </div>
              `;

              if (prd.handle != "swims-gift-card") {
                $(
                  '.c-collection__swatch-wrapper[data-product-id="' +
                    prd.id +
                    '"]'
                ).html(htmlColor);
              }
            }
          }
        },
      });

      setTimeout(() => {
        $("#collection_body").add(".loading-overlay").removeClass("loading");
      }, 300);
    }

    renderProduct(product) {
      if (!product.variants.length) {
        return;
      }

      let featured_image = product.images.filter((image) =>
        image.alt != null ? image.alt.indexOf("background::white") > -1 : ""
      );

      let hover_image =
        featured_image.length > 1
          ? featured_image[1].src
          : featured_image[0].src;
      featured_image = featured_image.length ? featured_image[0].src : "";
      let color_option = product.options.filter(
        (option) => (option.name = "Colour")
      );
      let color_name = color_option.length ? color_option[0].values[0] : "";
      let product_availability = "c-product__availability--unavailable";
      if (
        product.variants.some(
          (variant) =>
            variant.inventory_quantity > 0 ||
            variant.inventory_policy == "continue"
        )
      ) {
        product_availability = "";
      }

      let onNew = false;
      let final_sale = false;
      let extra_sale = false;
      let tags = product.tags;
      let tagsArr = tags.split(", ");
      let cp = Shopify.formatMoney(product.variants[0].compare_at_price);
      let pr = Shopify.formatMoney(product.variants[0].price);
      cp = cp.replace(".00", "");
      pr = pr.replace(".00", "");

      let on_sale =
        parseInt(product.variants[0].compare_at_price) >
        parseInt(product.variants[0].price);
      let compare_at_price = `<span class="c-product-tile__compare-at-price c-compare-at-price okay js-product-compare-price${
        on_sale ? " is-enabled" : ""
      }">${Shopify.formatMoney(
        product.variants[0].compare_at_price * 100
      )}</span>`;

      let percent_off = "";
      if (on_sale) {
        percent_off = `(${Math.round(
          ((parseInt(product.variants[0].compare_at_price) -
            parseInt(product.variants[0].price)) *
            100) /
            parseInt(product.variants[0].compare_at_price)
        )}% off)`;
      }

      if (tags.includes("final_sale") && on_sale) {
        final_sale = true;
      }

      if (tags.includes("extra_sale")) {
        extra_sale = true;
      }

      if (tags.includes("new")) {
        tagsArr.filter((tag) => {
          if (tag == "new") {
            onNew = true;
          }
        });
      }

      let discount_label = "";
      
      for (const key in discountsLabels) {
        if (tags.includes(discountsLabels[key].tag)) {
          discount_label = discountsLabels[key].message;
          break;
        }
      }
      
      let final_sale_message = document.querySelector('.collection.c-collection').getAttribute('data-final-sale-message');
      let discount_label_html = `${discount_label}`;

      let html = `
      <div class="js-product-body">
				<div class="c-product-tile">`;

      html += `<div class="product-badges">
                  ${
                    final_sale
                      ? '<div class="badge final-sale">Final sale</div>'
                      : ""
                  }
                  ${onNew ? '<div class="badge new">New</div>' : ""}
                  ${
                    on_sale && !final_sale
                      ? '<div class="badge sale">On sale</div>'
                      : ""
                  }
                </div>`;

      html += `<a href="/collections/${this.collection}/products/${
        product.handle
      }" title="${product.title}" 
        class="c-product-tile__image-link o-full-background-image js-product-tile-image can-hover" >

          <img data-src="${featured_image}" class="c-product-tile__image-link o-ratio o-full-background-image js-product-tile-image base" 
          src="${featured_image}">
          <img data-src="${hover_image}" data-hover="${hover_image}" class="c-product-tile__image-link o-ratio o-full-background-image hover" 
          src="${hover_image}">
          
          <form id="c-product-form-${
            product.id
          }" class="c-product-tile__favorites-form" data-product-handle="${
        product.handle
      }" data-variant-selector='{"reset_selection":true}' data-color-option="${
        color_option.name
      }" action="/cart/add" method="post">
						<input type="hidden" name="id" value="${product.variants[0].id}"/>
						<div class="gift-reggie-wishlist-button"></div>
					</form>
					</a>
					<div class="c-product-tile__details">
						<span class="c-product-tile__color c-product-tile__text js-product-tile-color">${color_name}</span>
						<a href="/collections/${this.collection}/products/${product.handle}" title="${
        product.title
      }" class="c-product-tile__title-link is-discrete">
							<h2 class="c-product-tile__title">${product.title}</h2>
						</a>
						<span class="c-product-tile__price">
							<div class="c-product__availability ${product_availability} js-product-availability">
								<p class="c-product-tile__price-currency">
									${compare_at_price}
									<span class="c-product-tile__price c-price js-product-price">${Shopify.formatMoney(
                    product.variants[0].price * 100
                  )}</span>
									<span class="c-product-tile__currency c-currency">${
                    Shopify.currency.active
                  }</span>
                  <span class="pdp-discount"></span>
                  <span class="discount-percentage">${
                    on_sale ? percent_off : ""
                  }</span>
								</p>
								<span class="c-product-tile__out-of-stock c-out-of-stock">Sold Out</span>
							</div>
						</span>
            <div class="final-sale-text">${final_sale ? final_sale_message : ""}</div>
            <div class="discount-label"> ${
              discount_label_html ? discount_label_html : ""
            } </div>
					</div>
				</div>
			`;

      html += `<div class="c-collection__swatch-wrapper c-option" data-product-id="${product.id}"></div></div>
              <div class="yotpo bottomLine" data-product-id="${product.id}"></div>`;
      return `<li class="c-collection__product-tile o-layout__item u-1/2 u-1/3@m js-product-tile" data-product-handle="${product.handle}">${html}</li>`;
    }
  }

  class FiltersUI {
    constructor(filters) {
      this.$filters = $(filters);
      this.$activeFilters = $("[data-filters-active]", this.$filters);
      this.$controls = $("[data-filter-control]", this.$filters);
      this.$types = $("[data-filter-type]", this.$filters);
      this.$tabs = $("[data-filter-tab]", this.$filters);
      this.$setsContainer = $(".filters__sets", this.$filters);
      this.$sets = $("[data-filter-option]", this.$filters);

      this.states = {
        active: "filters-ui--active",
      };

      this.$controls.on("click", (event) =>
        this.toggleFilterTypes(event.currentTarget)
      );
      this.$tabs.on("click", (event) =>
        this.toggleFilterTabs(event.currentTarget)
      );
    }

    toggleFilterTypes(control) {
      let $control = $(control),
        open = !$control.hasClass(this.states.active);
      this.$controls.removeClass(this.states.active);
      this.$types.removeClass(this.states.active);
      if (open) {
        this.$activeFilters.parent().removeClass(this.states.active);
        $control.addClass(this.states.active);
        this.$types
          .filter('[data-filter-type="' + control.dataset.filterControl + '"]')
          .addClass(this.states.active);

        if (window.innerWidth < 1025) {
          $("body").addClass("no-scroll");
        }
      } else if (this.$activeFilters.children().length) {
        this.$activeFilters.parent().addClass(this.states.active);
        if (window.innerWidth < 1025) {
          $("body").removeClass("no-scroll");
        }
      }
    }

    toggleFilterTabs(tab) {
      let $tab = $(tab),
        open = !$tab.hasClass(this.states.active);
      this.$tabs.removeClass(this.states.active);
      this.$sets.removeClass(this.states.active);
      this.$setsContainer.toggleClass(this.states.active, open);
      if (open) {
        $tab.addClass(this.states.active);
        this.$sets
          .filter('[data-filter-option="' + tab.dataset.filterTab + '"]')
          .addClass(this.states.active);
      }
    }
  }

  $(() => {
    document.querySelectorAll("form[data-filters]").forEach((form) => {
      var filters = new Filters(form);
      if (theme.current_object && theme.current_object.handle) {
        filters.collection = theme.current_object.handle;
      }
      filters.renderOptions();
      new FiltersUI(form);
    });
  });
})((window.theme = window.theme || {}), jQuery);

(($, theme) => {
  $(() => {
    let $container = $(".js-account-page");
    let $recovery_form = $(".js-recovery-form form", $container);
    let $password_reset_button = $(".js-account-recovery", $container);
    let $email_input = $('input[type="email"]', $recovery_form);
    let $account_message = $(".js-recovery-messages", $container);
    let $success_message = $account_message.data("recovery-success");
    let $error_message = $account_message.data("recovery-error");

    // Sends the recovery password form with the customer's email
    if (theme.customer) {
      $email_input.val(theme.customer.email);

      $password_reset_button.click(() => {
        $.post($recovery_form.attr("action"), $recovery_form.serialize())
          .done((resp) => {
            $account_message.text($success_message);
          })
          .fail((resp) => {
            $account_message.text($error_message);
          });
      });
    }
  });
})(jQuery, window.theme);

((theme, $) => {
  let current_page = 1;
  let $scroll_more = $(".js-scroll-more-articles");

  $(() => {
    let $article_grid = $(".js-articles");
    $scroll_more = $(".js-scroll-more-articles");
    current_page = +$article_grid.data("page");

    $(document).on("click", ".js-scroll-more-articles", () => {
      scrollMoreArticles($article_grid);
    });
  });

  function scrollMoreArticles($article_grid) {
    let next_page = current_page + 1;

    $scroll_more.hide();
    loadMoreArticles($article_grid, next_page);
  }

  function loadMoreArticles($article_grid, next_page) {
    let blog = $article_grid.data("blog");
    let url = $(".js-blog-tag.is-active").data("tag");

    $.get(`${url}?view=article-grid&page=${next_page}`, (template) => {
      let $grid = $(template).find(".js-articles").children();
      $article_grid.append($grid);

      current_page = next_page;

      if (!$(template).find(".js-scroll-more-articles").length) {
        $scroll_more.remove();
      } else {
        $scroll_more.show();
      }
    });
  }
})((window.theme = window.theme || {}), jQuery);

/**
 * CART FLYOUT LIBRARY
 *
 * @description Handles the Shopify cart flyout interactions.
 * @namespace cartflyout
 */

((theme, $) => {
  /**
   * Class representing a Cart Item in the cart flyout.
   *
   * @classdesc Creates a Cart Item objects from <code>.js-cart-flyout</code> children elements with the class <code>js-cart-item</code> and the <code>data-key</code> attribute containing the line item's key.
   * <br><br>
   *
   * A cart item requires an <code><input type="number"/></code> quantity field with the class <code>js-cart-qty</code>, an element with the class <code>js-cart-remove</code> for removing the item from the cart and an element with the class <code>js-cart-save</code> for saving to Wishlist.
   *
   * @example
   * <li class="js-cart-item" data-key="">
   * 	<input type="number" class="js-cart-qty"/>
   * 	<div class="js-cart-remove"></div>
   * 	<div class="js-cart-save"></div>
   * </li>
   *
   * @property {object} item - Cart line item
   * @property {object} elements - Contains the cart item element and its triggers
   * @property {string} key - Cart line item key
   */
  class CartItem {
    /**
     * Creates an instance of CartItem.
     *
     * @param {HTMLElement} $element - The line item element
     * @constructs CartItem
     * @memberof CartItem
     */
    constructor($element) {
      if (!$element) {
        console.error("CartItem.constructor: No element was passed.");
        return false;
      }

      this.elements = {
        $item: $element,
        $qty: $element.find(".js-cart-qty"),
        $remove: $element.find(".js-cart-remove"),
      };

      this.key = this.elements.$item.data("key");

      if (theme.cart.content.items) {
        this.item = theme.cart.content.items.filter((item) => {
          return item.key == this.key;
        })[0];
      }

      this._bindTriggers();
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Internal method that binds cart item triggers and adds them to <code>elements.qty</code>,  <code>elements.remove</code>, and <code>elements.save</code>
     * Handles change and click events.
     *
     * @private
     * @memberof CartItem
     */
    _bindTriggers() {
      this.elements.$qty
        .off("change.CartFlyout")
        .on("change.CartFlyout", () => {
          var qty = this.elements.$qty.val();

          if (qty > 0) {
            theme.cart
              .update({ id: this.key, quantity: this.elements.$qty.val() })
              .then(() => {
                $(document).trigger("CartItem:update");
              });
          } else {
            this.elements.$remove.trigger("click.CartFlyout");
          }
        });

      this.elements.$remove
        .off("click.CartFlyout")
        .on("click.CartFlyout", () => {
          theme.cart.remove(this.key).then(() => {
            $(document).trigger("CartItem:remove");
          });
        });
    }
  }

  /**
   * Class representing the cart flyout element.
   *
   * @classdesc Creates Cart Flyout object from the first (and only) element with the class <code>js-cart-flyout</code>.
   *
   * @example
   * <div class='js-cart-flyout'></div>
   *
   *
   * @property {object} elements - Contains the cart flyout, its triggers, and the items in the cart
   * @property {boolean} refresh - Flag to prevent the flyout from refreshing its layout too quickly while the user is still interacting with it
   */

  class CartFlyout {
    /**
     * Creates an instance of CartFlyout.
     *
     * @param {HTMLElement} $cart_flyout - The cart flyout element
     * @constructs CartItem
     * @memberof CartItem
     */
    constructor($cart_flyout) {
      if (!$cart_flyout) {
        console.error("CartFlyout.constructor: No element was passed.");
        return false;
      }

      this.elements = {
        cart_flyout: $cart_flyout,
        wrapper: $cart_flyout.parent(),
      };

      this.cart_items = [];
      this.flyout = new theme.Flyout(this.elements.cart_flyout);

      $cart_flyout.on("click", (event) => {
        if (event.currentTarget != event.target) {
          return;
        }
        this.flyout.close();
      });

      this._bindItems();

      $(document)
        .off("Cart:set")
        .on("Cart:set", () => {
          this._refresh();
        });

      $(document)
        .off("Cart:add")
        .on("Cart:add", () => {
          this.flyout.open();
        });
    }

    // ------------------------------
    //  Methods
    // ------------------------------

    /**
     * Internal method instantiates cart items adds them to <code>cartflyout.elements.cart_items</code>.
     *
     * @private
     * @memberof CartFlyout
     */
    _bindItems() {
      this.elements.cart_flyout.find(".js-cart-item").each((index, element) => {
        this.cart_items.push(new CartItem($(element)));
      });

      setTimeout(() => {
        let target = 0;
        const buttons = $(".scroll-button");
        const cartItems = $(".c-cart-items");

        const nextButton = $(".scroll-button#next");
        const prevButton = $(".scroll-button#prev");
        const itemScroll = 2;

        function isScrollAtBottom() {
          const scrollThreshold =
            prevButton.outerHeight() + nextButton.outerHeight();
          return (
            cartItems.scrollTop() + cartItems.height() + scrollThreshold >=
            cartItems[0].scrollHeight
          );
        }

        function updateButtonState() {
          if (isScrollAtBottom()) {
            nextButton.addClass("disabled");
          } else {
            nextButton.removeClass("disabled");
          }

          if (target <= 0) {
            prevButton.addClass("disabled");
          } else {
            prevButton.removeClass("disabled");
          }
        }

        buttons.each(function () {
          $(this).click(function (e) {
            e.preventDefault();
            let cartItemHeight =
              $(".c-cart-items__item").first().outerHeight() * itemScroll;
            let id = $(this).attr("id");

            if (id == "next") {
              target = target + cartItemHeight;
            } else {
              target = target - cartItemHeight;
            }
            cartItems.stop().animate({
              scrollTop: target,
            });
            updateButtonState();
          });
        });

        cartItems.on("scroll", function () {
          target = $(this).scrollTop();
          updateButtonState();
        });

        updateButtonState();
      }, 1000);
    }

    /**
     * Internal method that updates the contents of the Cart Flyout.
     *
     * @private
     * @memberof CartFlyout
     */
    _refresh() {
      $(".js-cart-item-count").text(theme.cart.content.item_count || "");
      $.get("/cart?view=flyout", (cart_flyout) => {
        var $cart_flyout = $(cart_flyout);
        this.elements.cart_flyout.html($cart_flyout);
        this.flyout._bindTriggers();

        this._bindItems();
        this.elements.cart_flyout.trigger("CartFlyout_update");
      });
    }
  }

  // ------------------------------
  //  Initiallization
  // ------------------------------
  if (!theme.cart_flyout) {
    theme.cart_flyout = {};
  }

  theme.cart_flyout.cart = new CartFlyout($(".js-cart-flyout").first());
})((window.theme = window.theme || {}), jQuery);

(($) => {
  $(() => {
    const setProductQuantity = ($container, $qty_element) => {
      let $product_input = $container.find(".js-cart-qty");
      let value = parseInt(+$product_input.val()); // The '+' sign converts the value to a number
      let min = parseInt($product_input.attr("min"));
      let max = parseInt($product_input.attr("max"));
      if (isNaN(min)) {
        min = 0;
      }
      if (isNaN(max)) {
        max = 100;
      }

      if ($qty_element.hasClass("js-qty-inc")) {
        value = Math.min(++value, max);
      } else if ($qty_element.hasClass("js-qty-dec")) {
        value = Math.max(--value, min, 0);
      } else {
        value = Math.max(Math.min(value, max), 0);
      }
      $container.find(".js-qty-dec").prop("disabled", value == min);
      $container.find(".js-qty-inc").prop("disabled", value == max);

      $product_input.val(value).trigger("change.CartFlyout");
    };

    $("#cart, #cart_flyout").on(
      "click",
      ".js-qty-inc, .js-qty-dec",
      function (e) {
        e.preventDefault();
        setProductQuantity($(this).closest(".js-qty-container"), $(this));
      }
    );
    $(".js-qty-container").each(function () {
      setProductQuantity($(this), $(this).find(".js-cart-qty"));
    });
  });
})(jQuery);

(($) => {
  $(".js-country-switcher").on("change", (event) => {
    let url = event.target.value,
      country_code = sessionStorage.getItem("_country_code");
    if (country_code) {
      url +=
        (url.indexOf("?") > -1 ? "&" : "?") + "_from_country=" + country_code;
    }
    window.location.replace(url);
  });
})(jQuery);

(($, theme) => {
  $(() => {
    (() => {
      // For the footer newsletter, reset the form when the user clicks the retry button
      const $retry_button = $(".js-footer-newsletter-retry");
      $retry_button.on("click", () => {
        $retry_button
          .closest(".js-footer-newsletter-messaging")
          .removeClass("is-visible");
      });
    })();

    (() => {
      // Validate the email input and disable error messages

      const $newsletter_form = $(".js-footer-newsletter-form");
      const $newsletter_tooltip = $(".js-footer-tooltip");

      // Show the tooltip with an error message
      function show_footer_error(message) {
        $(".js-tooltip-message", $newsletter_tooltip).text(message);
        $newsletter_tooltip.addClass("is-visible");
      }

      // Hide the tooltip on click
      $newsletter_tooltip.on("click", () => {
        $newsletter_tooltip.removeClass("is-visible");
      });

      $newsletter_form.on("submit", () => {
        const email = $(".js-footer-newsletter-email", $newsletter_form).val();

        // Check if the email field is blank or valid
        if (!email || !email.trim()) {
          show_footer_error(theme.locales.footer.blank_email);
          return false;
        } else if (
          !email
            .trim()
            .match(
              /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
            )
        ) {
          show_footer_error(theme.locales.footer.invalid_email);
          return false;
        }
      });
    })();
  });
})(jQuery, window.theme);

((theme, $) => {
  $(() => {
    let $featuredCollections = $(".js-featured-collections");

    $featuredCollections.each(function () {
      let $slider = $(this).find(".js-featured-collection-slider");

      // Pre-filter the slides
      let first_filter =
        $slider
          .find(".js-featured-collection-slide")
          .map(function () {
            return $(this).attr("data-collection-filtering");
          })
          .get()
          .filter(function (value, index, self) {
            return self.indexOf(value) === index;
          })[0] || $(".js-tab-option:checked").val();

      // Cache selectors
      let $this = $(this),
        $tabs = $this.find(".js-tab-option");

      // Add enter key to the basic click event
      $tabs.on("keydown", function (e) {
        if (e.which == 13) {
          $(this).change();
        }
      });

      $slider.slick({
        dots: true,
        arrows: false,
        draggable: true,
        mobileFirst: true,
        infinite: false,
        slidesToScroll: 1,
        slidesToShow: 1,
        swipe: true,
        responsive: [
          {
            breakpoint: window.theme.bp.m,
            settings: {
              slidesToShow: 3,
              dots: false,
            },
          },
        ],
      });

      if (first_filter) {
        $slider
          .slick("slickUnfilter")
          .slick(
            "slickFilter",
            `.js-featured-collection-slide[data-collection-filtering=${first_filter}]`
          );
        $(`.js-slider-cta[data-collection-filtering=${first_filter}]`).show();
      }
    });

    $(".js-tab-option").on("change", function () {
      let $this = $(this);
      let selected_collection = $this.val();
      let $label = $(`[for="${$this.attr("id")}"]`);
      let $slider = $this
        .closest(".js-featured-collections")
        .find(".js-featured-collection-slider");

      let $ctas = $(`.js-slider-cta`);
      let $shown_cta = $ctas.filter(
        `[data-collection-filtering=${selected_collection}]`
      );

      $ctas.hide();
      $shown_cta.show();
      updateLabelAccessibility($label, $slider);

      $slider
        .slick("slickUnfilter")
        .slick(
          "slickFilter",
          `.js-featured-collection-slide[data-collection-filtering=${selected_collection}]`
        );

      // Refresh the pairs with slider when switching tabs
      var $pairs_with_slider = $(".js-pairs-with-slider", $shown_cta);
      if ($pairs_with_slider.length) {
        $pairs_with_slider[0].slick.setPosition();
      }

      $(".gift-reggie-wishlist-button", $slider)
        .filter(":empty")
        .each(function () {
          window.GiftReggie.publishButton($(this));
        });
    });

    function updateLabelAccessibility($label, $slider) {
      // De-select all the tabs
      $label.attr("aria-selected", "false");
      $(".js-tab-item").removeClass("is-active");

      // Select this tab
      $label
        .attr("aria-selected", "true")
        .closest(".js-tab-item")
        .addClass("is-active");
    }
  });
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  $(() => {
    let $product_slider = $(".js-homepage-product-slider");
    let $prev = $product_slider.siblings(".js-slider-left");
    let $next = $product_slider.siblings(".js-slider-right");

    $product_slider
      .on("beforeChange", function (event, slick, currentSlide, nextSlide) {
        $prev.toggleClass("is-disabled", nextSlide === 0);
        $next.toggleClass(
          "is-disabled",
          nextSlide === slick.slideCount - slick.options.slidesToShow
        );
      })
      .slick({
        centerMode: false,
        dots: false,
        draggable: true,
        mobileFirst: true,
        infinite: false,
        nextArrow: $next,
        prevArrow: $prev,
        slidesToScroll: 1,
        slidesToShow: 2,
        swipe: true,
      });
  });
})((window.theme = window.theme || {}), jQuery);

// TODO: Find a workaround for IE11 --> Does not detect 'animationend'
// ($ => {
// Force animation complete
// $(() => {
// 	let $animatedLink = $('.o-cta-link');
// 	$animatedLink.on({
// 		'mouseenter': function() {
// 			$(this).addClass('hover');
// 		},
// 		'webkitAnimationEnd oanimationend msAnimationEnd animationend': function() {
// 			$(this).removeClass('hover');
// 		}
// 	});
// });
// })(jQuery);

(($) => {
  /**
   * Creates and manages a list of images from instagram.
   * Displays as a grid on desktop and becomes a slick slider
   * on mobile.
   * @class
   */
  class HomepageInstagramFeed {
    /**
     * @constructs HomepageInstagramFeed
     * @param {jQuery} $container - The container to hold instagram images
     */
    constructor($container) {
      this.$container = $container;
      this.$window = $(window);

      this.breakpoint = window.theme.bp.m;
      this.instagram_data = $container.data("instagram");

      this._initInstagram();
      this._setupLinkListeners();
    }

    /**
     * Initialize the instafeed object
     * based on provided settings
     * @private
     * @memberof HomepageInstagramFeed
     */
    _initInstagram() {
      const feed = new Instafeed({
        get: "user",
        userId: parseInt(this.instagram_data.user_id),
        target: this.$container[0].id,
        accessToken: this.instagram_data.access_token,
        resolution: "standard_resolution",
        limit: parseInt(this.instagram_data.limit),
        template:
          '<div class="c-instagram-feed__image"><a href="{' +
          "{link}" +
          '}" class="js-instagram-link o-full-background-image o-ratio" style="background-image: url(\'{' +
          "{image}" +
          '}\');" target="_blank"></a></div>',
        after: this._instragramLoaded.bind(this),
      });
      feed.run();
    }

    /**
     * After instafeed has loaded, initialize
     * slick if required
     * @private
     * @memberof HomepageInstagramFeed
     */
    _instragramLoaded() {
      if (!this.$container.hasClass("slick-initialized")) {
        this._initSlick();
        this._registerResizeListeners();
      }
    }

    /**
     * Initialize the slick slider
     * @private
     * @memberof HomepageInstagramFeed
     */
    _initSlick() {
      this.current_slide = 0;

      this.$container
        .on("afterChange", (event, slick, current_slide) => {
          this.current_slide = current_slide; // Update the current slide after it changes
        })
        .slick({
          arrows: false,
          centerMode: true,
          centerPadding: "23px",
          infinite: true,
          focusOnSelect: true,
          mobileFirst: true,
          responsive: [
            {
              /**
               * Slick's built in responsive settings will destroy
               * the slider on desktop, but we will need to reinitalize it
               * ourselves on mobile
               */
              breakpoint: this.breakpoint,
              settings: "unslick",
            },
          ],
        });
    }

    /**
     * Setup the window resize listener
     * @private
     * @memberof HomepageInstagramFeed
     */
    _registerResizeListeners() {
      this.$window.on(
        "resize",
        $.throttle(250, () => {
          this._resize();
        })
      );
    }

    /**
     * Check if we need to reinitialize slick
     * when switching to mobile
     * @private
     * @memberof HomepageInstagramFeed
     */
    _resize() {
      let desktop_enabled = this.$window.width() >= this.breakpoint;
      if (!desktop_enabled && !this.$container.hasClass("slick-initialized")) {
        this._initSlick();
      }
    }

    /**
     * If the user clicks the link of a slide that isn't in view,
     * prevent the default link behaviour so that slick slides that slide
     * into view
     * @private
     * @memberof HomepageInstagramFeed
     */
    _setupLinkListeners() {
      this.$container.on("click", ".js-instagram-link", (event) => {
        if (
          this.$container.hasClass("slick-initialized") &&
          parseInt($(event.currentTarget).parent().data("slickIndex")) !==
            this.current_slide
        ) {
          event.preventDefault();
        }
      });
    }
  }

  $(() => {
    // Setup each instafeed section
    $(".js-instafeed").each((index, element) => {
      new HomepageInstagramFeed($(element));
    });
  });
})(jQuery);

((theme, $) => {
  function initialize() {
    let $journal_gallery = $(".js-journal-sec-gallery");

    $journal_gallery.slick({
      arrows: false,
      adaptiveHeight: true,
      mobileFirst: true,
      centerMode: false,
      dots: true,
      draggable: true,
      slidesToScroll: 1,
      slidesToShow: 1,
      swipe: true,
      dotsClass: "slick-dots is-discrete",
    });
  }

  // Initialize on page load
  $(() => {
    initialize();
  });

  // Also initialize on section load for the customizer
  $(document).on("shopify:section:load", () => {
    initialize();
  });
})((window.theme = window.theme || {}), jQuery);

/**
 * Logic to handle opening of recovery form on the login/registration page
 * as well as the tabbed menu on mobile
 */

(($) => {
  $(() => {
    let $container = $(".js-login-page");

    let $login_form = $(".js-login-form", $container);
    let $recovery_form = $(".js-recovery-form", $container);

    let $login_focus = $(".js-open-focus", $login_form);
    let $recovery_focus = $(".js-open-focus", $recovery_form);

    let $recover_open = $(".js-recover-open", $container);
    let $recover_close = $(".js-recover-close", $container);
    let $recover_success = $(".js-recovery-success", $recovery_form);

    // Open the recovery form while closing the login form
    $recover_open.on("click", (event) => {
      event.preventDefault();

      $login_form.slideUp();
      $recovery_form.slideDown();
      $recovery_focus.focus();
    });

    // Close the recovery form while opening the login form
    $recover_close.on("click", (event) => {
      event.preventDefault();

      $recovery_form.slideUp();
      $login_form.slideDown();
      $login_focus.focus();
    });

    let hash = window.location.hash;

    // Allow deep linking to recover password form
    if (hash === "#recover" || $recover_success.length > 0) {
      $recovery_form.show();
      $login_form.hide();
    }
  });
})(jQuery);

((theme, $) => {
  function initialize() {
    setTimeout(function () {
      $(".c-product__gallery").addClass("visible");
    }, 300);

    let $product_gallery = $(".js-product-gallery");
    let $product_gallery_nav = $(".js-product-gallery-nav");

    $product_gallery
      .slick({
        arrows: false,
        dots: true,
        draggable: true,
        mobileFirst: true,
        slidesToScroll: 1,
        slidesToShow: 1,
        swipe: true,
        focusOnSelect: true,
        responsive: [
          {
            breakpoint: window.theme.bp.m,
            settings: {
              vertical: true,
              dots: false,
              verticalSwiping: false,
            },
          },
        ],
      })
      .on("afterChange", function (e, slick, currentSlide) {
        var currentSlideIndex = $(currentSlide).attr("data-slick-index");
        $(".js-product-gallery-nav-slide.slick-active").removeClass(
          "slick-current"
        );
        $(
          '.js-product-gallery-nav-slide.slick-active[data-slick-index="' +
            currentSlide +
            '"]'
        ).addClass("slick-current");
      });

    if ($(window).width() > window.theme.bp.m) {
      $product_gallery_nav.slick({
        slidesToShow: 4,
        slidesToScroll: 1,
        focusOnSelect: true,
        asNavFor: $product_gallery,
        arrows: false,
        dots: false,
        draggable: true,
        mobileFirst: true,
        responsive: [
          {
            breakpoint: window.theme.bp.m,
            settings: {
              vertical: true,
              verticalSwiping: true,
            },
          },
        ],
      });
    }

    let current_color = $product_gallery.data("current-color");
    if (current_color) {
      $product_gallery
        .slick("slickUnfilter")
        .slick(
          "slickFilter",
          `.js-product-gallery-slide[data-color="${current_color}"]`
        );
    }

    if ($(window).width() > window.theme.bp.m) {
      let current_nav_color = $product_gallery_nav.data("current-color");

      if (current_nav_color) {
        $product_gallery_nav
          .slick("slickUnfilter")
          .slick(
            "slickFilter",
            `.js-product-gallery-nav-slide[data-color="${current_color}"]`
          );
      }
    }
  }

  const $product_info = $(".js-product-info");
  const $variant_selector = $(".js-product-container", $product_info);

  // Setup the add to cart button's state manager
  function setupATCManager() {
    const $atc_button = $(".js-atc-button", $product_info);
    if ($atc_button.length === 0) {
      return;
    }

    const atc_state_manager = new theme.ATCStateManager($atc_button, {
      variant_selector: {
        strings: {
          out_of_stock: theme.locales.variant_selector.out_of_stock,
          incomplete: theme.locales.variant_selector.select_size,
        },
      },
      cart: {
        strings: {
          in_cart: theme.locales.variant_selector.max_quantity_cart,
        },
      },
      strings: {
        atc: theme.locales.variant_selector.atc,
      },
    });

    theme.atc_state_manager = atc_state_manager;
  }

  function reIndex() {
    var indx = 0;
    $.each(
      $(".js-product-gallery-nav-slide").not(".slick-cloned"),
      function (t, v) {
        $(v).attr("data-slick-index", indx);
        indx++;
      }
    );
  }

  // Update the color label based on the selected color
  function setupColorUpdater() {
    const $color_label = $(".js-color-label", $product_info);
    const $size_selector = $(".js-size-selector", $product_info);
    const color_option = $variant_selector.data("color-option");

    $variant_selector.on(
      "variant-selector__option-updated--" + color_option,
      (event, value) => {
        let $slider = $(".js-product-gallery");
        let $slider_nav = $(".js-product-gallery-nav");
        $color_label.text(value);
        $size_selector.prop("checked", false); // Reset the size selector

        $slider.slick("slickGoTo", 0);
        $slider.slick("slickUnfilter");
        $slider.slick(
          "slickFilter",
          `.js-product-gallery-slide[data-color="${value}"]`
        );

        $slider_nav.slick("slickGoTo", 0);
        $slider_nav.slick("slickUnfilter");
        $slider_nav.slick(
          "slickFilter",
          `.js-product-gallery-nav-slide[data-color="${value}"]`
        );

        reIndex();
      }
    );
  }

  // Disable or enable size options based on the availability of products after an update
  function setupSizeUpdater() {
    const variant_selector =
      theme.product_vs[$variant_selector.data("product-handle")];
    const size_property = "Size"; // or "SIZE", "SIZe", "size", etc.
    const size_regex = new RegExp(size_property, "i");
    let size_option =
      variant_selector?.variant_selector?.variant_options ??
      "No size available";

    if (typeof size_option === "object") {
      const keys = Object.keys(size_option);
      for (let key of keys) {
        if (size_regex.test(key)) {
          size_option = size_option[key];
          break;
        }
      }
    }

    if (size_option) {
      const size_map = $(".js-size-option-value", $product_info)
        .get()
        .reduce((acc, cur) => ((acc[cur.value] = $(cur)), acc), {});
      function updateSizeOptions() {
        Object.keys(size_option.values).forEach((size) => {
          let disable = !size_option.values[size].activated;
          let unavailable =
            size_option.values[size].associated_variants.length === 1 &&
            !size_option.values[size].associated_variants[0].available;

          size_map[size].prop("disabled", disable);
          unavailable
            ? size_map[size].addClass("is-unavailable")
            : size_map[size].removeClass("is-unavailable");
        });
      }

      $variant_selector.on(
        "variant-selector__selection-update",
        updateSizeOptions.bind(this)
      );

      updateSizeOptions();
    }
  }

  // Update the price based on the selected variant
  function setupPriceUpdater() {
    const $product_availability = $(".js-product-availability", $product_info);
    const $product_price = $(".js-product-price", $product_info);
    const $product_compare_price = $(
      ".js-product-compare-price",
      $product_info
    );

    $variant_selector.on(
      "variant-selector__selection-update",
      (event, variants) => {
        let variants_length = variants.length;
        let initial_variant_index = 0;

        if (variants_length === 0) {
          return;
        }

        for (var i = 0; i < variants_length; i++) {
          if (variants[i].available) {
            initial_variant_index = i;
            break;
          }
        }

        $product_availability.toggleClass(
          "c-product__availability--unavailable",
          !variants[initial_variant_index].available
        );
        $product_price.text(variants[initial_variant_index].price_formatted);

        if ($.isNumeric(variants[initial_variant_index].compare_at_price)) {
          $product_compare_price
            .text(variants[initial_variant_index].compare_at_price_formatted)
            .toggleClass(
              "is-enabled",
              variants[initial_variant_index].compare_at_price >
                variants[initial_variant_index].price
            );
          $(".pdp-sale-badge").show();
          var percentageOff =
            variants[initial_variant_index].compare_at_price -
            variants[initial_variant_index].price;
          percentageOff = percentageOff * 100;
          percentageOff = Math.round(
            percentageOff / variants[initial_variant_index].compare_at_price
          );
          $(".pdp-discount").text("(" + percentageOff + "% off)");
        } else {
          $product_compare_price.text("").removeClass("is-enabled");
          $(".pdp-sale-badge").hide();
          $(".pdp-discount").empty();
        }
      }
    );
  }

  function setupCartButton() {
    let is_selection_completed = false;
    let options_warnings = $(".js-option-warning", $variant_selector);
    const size_error_string =
      theme.locales.variant_selector.option_warnings.size_error;

    $variant_selector
      .on("variant-selector__selection-update", (event, variants, complete) => {
        is_selection_completed = complete;
        if (is_selection_completed) {
          options_warnings.removeClass("c-warning--error");
        }
      })
      .on("submit", function (e) {
        // Submit the product form asynchronously
        e.preventDefault();

        if (!is_selection_completed) {
          options_warnings.addClass("c-warning--error").text(size_error_string);
          return;
        }
        theme.cart
          .add(theme.utils.toObject($(this).serialize()))
          .done(function (data) {
            if (window.SideCart) {
              window.SideCart.updateSideCart(true);
            }
          });
      });
  }

  // Cart popup
  function cartPopup(data) {
    let price = data.price;
    if (data.compare_at_price > data.price) {
      price = data.compare_at_price;
    }
    if (data.original_price > data.price) {
      price = data.original_price;
    }

    let product_options = "";

    product_options += `
      <div class="item-quantity">
        <span>Quantity:</span>
        <span>${data.quantity}</span>
      </div>
    `;

    data.options_with_values.forEach((item) => {
      product_options += ` 
      <div class="item-option">
        <span>${item.name}:</span>
        <span>${item.value}</span>
      </div>`;
    });

    $(".cart-popup-item__title").html(data.product_title);
    $(".cart-popup-item__image-wrapper").html(`<img src="${data.image}" />`);
    $(".cart-popup-item__price").html(Shopify.formatMoney(price));
    $(".cart-popup-item__options").html(product_options);

    $(".cart-popup-wrapper").show();

    setTimeout(() => {
      $(".cart-popup-wrapper").hide();
    }, 3000);
  }

  // Handles the the info tabs opening and closing
  (function ($) {
    class InfoTab {
      constructor($tab) {
        this.$tab = $tab;
        this.$content = $(".js-info-tab-content", this.$tab);
        this.$header = $(".js-info-tab-header", this.$tab);

        this.$header.on("click", () =>
          this.$tab.hasClass("is-expanded") ? this.close() : this.open()
        );
      }
      close() {
        if (!this.$tab.hasClass("is-expanded")) {
          return;
        }
        this.$tab.removeClass("is-expanded");
        this.$content.slideUp("slow");
      }
      open() {
        if (this.$tab.hasClass("is-expanded")) {
          return;
        }

        close_all();
        this.$tab.addClass("is-expanded");
        this.$content.slideDown(() => this.$content.removeClass("is-hidden"));
      }
    }

    let info_tabs = $(".js-info-tab")
      .get()
      .map((element) => new InfoTab($(element)));

    function close_all() {
      info_tabs.forEach((tab) => tab.close());
    }
  })(jQuery);

  if ($product_info.length > 0) {
    setupATCManager();
  }

  // Initialize on page load
  $(() => {
    initialize();

    if ($product_info.length > 0) {
      setupColorUpdater();
      setupPriceUpdater();
      setupSizeUpdater();
      setupCartButton();
      reIndex();
    }
  });

  // Also initialize on section load for the customizer
  $(document).on("shopify:section:load", () => {
    initialize();
  });
})((window.theme = window.theme || {}), jQuery);

((theme, $) => {
  $(() => {
    var timer;

    class Swatch {
      constructor(
        $tile,
        $tile_body,
        swatch,
        $tile_image,
        $tile_image_hover,
        $tile_color,
        $tile_link,
        $tile_price,
        $tile_badges,
        $discount_label
      ) {
        this.$tile = $tile;
        this.$swatch = $(swatch);
        this.$tile_image = $tile_image;
        this.$tile_image_hover = $tile_image_hover;
        this.$tile_color = $tile_color;
        this.$tile_link = $tile_link;
        this.$tile_price = $tile_price;
        this.$discount_label = $discount_label;
        this.$tile_badges = $tile_badges;
        this.$tile_body = $tile_body;

        this.$tile_body.hover(
          () => {
            this.$swatch.next().on("mouseover", () => {
              clearTimeout(timer);
              this.productHover();
            });
          },
          () => {
            if (this.$swatch.is(":checked")) {
              this.$swatch_active = this.$swatch;
            }
          }
        );

        this.$swatch.next().on("mouseout", () => {
          this.productMouseout(this.$tile.find(".js-color-swatch:checked"));
        });

        this.$swatch
          .on("change", (e) => {
            this.select();
            this.$swatch_active = $(e.target);
          })
          .filter(":checked")
          .each(() => {
            this.select();
          });
      }

      select() {
        this.url = this.$swatch.attr("data-url");
        this.$tile_link.attr("href", this.url);

        this.$tile_color.text(this.$swatch.val());
        this.imageToggle(this.$swatch.val());
      }

      productHover() {
        this.url = this.$swatch.attr("data-url");
        this.$tile_link.attr("href", this.url);
        this.imageHover(this.$swatch);
      }

      productMouseout(checkedSwatch) {
        if (checkedSwatch) {
          var me = this;
          timer = setTimeout(function () {
            me.url = checkedSwatch.attr("data-url");
            me.$tile_link.attr("href", me.url);
            me.imageHover(checkedSwatch);
          }, 50);
        }
      }

      imageHover($swatch) {
        if ($swatch.attr("data-image") != 0) {
          this.$tile_image.attr("src", $swatch.attr("data-image"));
          this.$tile_image_hover.attr("src", $swatch.attr("data-image-hover"));
        }
      }

      imageToggle(meColor) {
        let prod_handle = this.$swatch.attr("data-handle");
        let _this = this;
        if (typeof prod_handle !== "undefined") {
          jQuery.getJSON(
            window.Shopify.routes.root + "products/" + prod_handle + ".js",
            function (product) {
              let onNew = false;
              let final_sale = false;
              let extra_sale = false;
              let tags = product.tags;
              let cp = Shopify.formatMoney(
                product.variants[0].compare_at_price
              );
              let pr = Shopify.formatMoney(product.variants[0].price);
              cp = cp.replace(".00", "");
              pr = pr.replace(".00", "");
              let on_sale =
                parseInt(product.variants[0].compare_at_price) >
                parseInt(product.variants[0].price);
              let compare_at_price = `<span class="c-product-tile__compare-at-price c-compare-at-price okay js-product-compare-price${
                on_sale ? " is-enabled" : ""
              }">${cp}</span>`;
              let product_availability = "c-product__availability--unavailable";
              if (product.available) {
                product_availability = "";
              }
              let percent_off = "";
              if (on_sale) {
                percent_off = `(${Math.round(
                  ((product.compare_at_price - product.price) * 100) /
                    product.compare_at_price
                )}% off)`;
              }

              if (tags.includes("final_sale") && on_sale) {
                final_sale = true;
              }

              if (tags.includes("extra_sale")) {
                extra_sale = true;
              }

              if (tags.includes("new")) {
                onNew = true;
              }
              let discount_label = "";
              let has_extra_sale_tag = false;

              for (const key in discountsLabels) {
                if (tags.includes(discountsLabels[key].tag)) {
                  discount_label = discountsLabels[key].message;

                  if (discountsLabels[key].tag == 'extra_sale') {
                    has_extra_sale_tag = true;
                  }
                  break;
                }
              }

              let html = `
              <span class="c-product-tile__price">
                <div class="c-product__availability ${product_availability} js-product-availability">
                  <p class="c-product-tile__price-currency">
                    ${compare_at_price}
                    <span class="c-product-tile__price c-price js-product-price ${
                      on_sale ? "is-red" : ""
                    }"> ${pr} </span>
                    <span class="c-product-tile__currency c-currency">${
                      Shopify.currency.active
                    }</span>
                    <span class="pdp-discount"></span>
                    <span class="discount-percentage">${
                      on_sale ? percent_off : ""
                    }</span>
                  </p>
                  <span class="c-product-tile__out-of-stock c-out-of-stock">Sold Out</span>
                </div>
              </span>
              `;

              let final_sale_html = `
              <div class="final-sale-text">${
                final_sale ? _this.$tile_sale_wrapper.attr("data-sale-text") : ""
              }</div>
              `;

              let final_badge_html = `
              <div class="badge final-sale">${
                final_sale ? "Final sale" : ""
              }</div>
              `;

              let new_badge_html = `
              <div class="badge new">${onNew ? "New" : ""}</div>
              `;
              let sale_badge_html = `
              <div class="badge sale">${on_sale ? "On sale" : ""}</div>
              `;

              let discount_label_html = null;

              if (!has_extra_sale_tag) {
                _this.$tile_sale.html(final_sale_html);
              }
              /*
              
              if (!final_sale) {
                discount_label_html = `${discount_label}`;
                _this.$discount_label.html(discount_label_html);
              }
              */

              _this.$tile_price.html(html);

              if (onNew) {
                _this.$tile_badges.find(".new").remove();
                _this.$tile_badges.prepend(new_badge_html);
              } else {
                _this.$tile_badges.find(".new").remove();
              }

              if (on_sale) {
                _this.$tile_badges.find(".sale").remove();
                _this.$tile_badges.prepend(sale_badge_html);
              } else {
                _this.$tile_badges.find(".sale").remove();
              }

              if (final_sale) {
                _this.$tile_badges.find(".final-sale").remove();
                _this.$tile_badges.prepend(final_badge_html);
              } else {
                _this.$tile_badges.find(".final-sale").remove();
              }

              let fields = product.media;
              let imageResults = [];
              if (meColor) {
                for (var i = 0; i < fields.length; i++) {
                  if (
                    fields[i].alt != null &&
                    fields[i].alt.toLowerCase().includes(meColor)
                  ) {
                    imageResults.push(fields[i]);
                  }
                }
              }
              if (imageResults.length > 0) {
                _this.$tile_image.attr("src", imageResults[0].src);
                _this.$tile_image_hover.attr("src", imageResults[0].src);
                if (imageResults[1]) {
                  _this.$tile_image_hover.attr("src", imageResults[1].src);
                }
              }
            }
          );
        }
      }
    }

    function loadSwatches() {
      let $tile = $(this).closest(".js-product-tile");
      let $tile_body = $(".js-product-body", $tile);
      let $tile_image = $(".js-product-tile-image", $tile);
      let $tile_image_hover = $(".js-product-tile-image-hover", $tile);
      let $tile_color = $(".js-product-tile-color", $tile);
      let $tile_link = $(
        "a.c-product-tile__image-link, .c-product-tile__title-link",
        $tile
      );
      let $swatches = $(".js-color-swatch", $tile);
      let $tile_price = $(".c-product-tile__price", $tile);
      let $tile_sale = $(".final-sale-text", $tile);
      let $tile_sale_wrapper = $(".final-sale-text-wrapper", $tile);
      let $tile_badges = $(".product-badges", $tile);
      let $discount_label = $(".discount-label", $tile);
      $swatches
        .get()
        .map(
          (swatch) =>
            new Swatch(
              $tile,
              $tile_body,
              swatch,
              $tile_image,
              $tile_image_hover,
              $tile_color,
              $tile_link,
              $tile_price,
              $tile_sale,
              $tile_sale_wrapper,
              $tile_badges,
              $discount_label
            )
        );
    }

    $(".js-product-tile").each(loadSwatches);
    $(".c-collection__filters").on("change", function () {
      setTimeout(() => {
        $(".js-product-tile").each(loadSwatches);
      }, 1200);
    });
  });
})((window.theme = window.theme || {}), jQuery);

(($) => {
  const NEWSLETTER_VALUE = "newsletter";
  let $newsletter_box = $(".js-register-page .js-register-newsletter");
  let $newsletter_flag = $(
    '[name="customer[accepts_marketing]"]',
    $newsletter_box
  );
  let $newsletter_tag = $('[name="customer[tags]"]', $newsletter_box);
  let $marketing_checkbox = $(".js-accept-marketing", $newsletter_box);

  $(() => {
    $marketing_checkbox.on("change", function () {
      $newsletter_flag.val(this.checked ? "1" : "");
      $newsletter_tag.val(this.checked ? NEWSLETTER_VALUE : "");
    });
  });

  // Group colors
  if (
    $(".template-collection").length != 0 ||
    $(".template-search").length != 0
  ) {
    $(".c-collection__product-tile").each(function () {
      let valueActive = $(this).find(".js-color-swatch[checked]").val();
      if ($(this).find(".js-color-swatch").length > 2) {
        renderColorProduct($(this), valueActive);
      }
    });

    function renderColorProduct(prd, valueActive) {
      let arrayColors = [];

      $(prd)
        .find(".js-color-swatch")
        .each(function () {
          arrayColors.push({
            url: $(this).data("url"),
            id: $(this).attr("id"),
            handle: $(this).data("handle"),
            tile_image: $(this).data("image"),
            tile_image_hover: $(this).data("image-hover"),
            colour: $(this).val().toLowerCase(),
          });
        });

      // Remove duplicates based on color.id
      let colors = Array.from(
        new Set(arrayColors.map((color) => color.id))
      ).map((id) => {
        return arrayColors.find((color) => color.id === id);
      });

      // Sort colors to have the active color first
      colors.sort((a, b) => {
        if (a.colour === valueActive) return -1;
        if (b.colour === valueActive) return 1;
        return 0;
      });

      const obj = {};
      let htmlColor = "";
      let count = 0;
      let limit = 4;
      htmlColor += `
      <div class="c-collection__colors c-option__colors o-swatch-list">`;

      colors.forEach((color, index) => {
        let color_state = "";
        for (const key in theme.color_filters) {
          if (theme.color_filters[key].includes(color.colour)) {
            if (!obj[key]) {
              obj[key] = color.colour;
              count = count + 1;
              if (color.handle == prd.data("product-handle")) {
                color_state = "checked";
              } else {
                color_state = "";
              }

              htmlColor += `
                <input class="o-swatch-input js-color-swatch
                ${count > limit ? "hide" : ""}" 
                data-handle="${color.handle}" 
                id="${color.id}" type="radio" name="${prd.data(
                "product-handle"
              )}-colour" 
                value="${color.colour}" data-url="${color.url}" 
                data-image="${color.tile_image}"
                data-image-hover="${color.tile_image_hover}"
                ${color_state}>

                <label data-handle="${color.handle}" 
                class="c-collection__option-color c-option__color o-swatch 
                swatch-${Shopify.handleize(color.colour)}" 
                for="${color.id}" title="${key}">${key}</label>
              `;
              break;
            }
          }
        }
      });

      colors.length > limit
        ? (htmlColor += `<span class="remaining-colors"> + ${
            colors.length - limit
          }</span>`)
        : "";
      htmlColor += `
        </div>
    `;

      $(
        '.c-collection__swatch-wrapper[data-product-id="' +
          prd.data("product-id") +
          '"]'
      ).html(htmlColor);
    }
  }
})(jQuery);

// handles product cards atc
(function ($) {
  // function cartPopup(data) {
  //   let price = data.price;
  //   if (data.compare_at_price > data.price) {
  //     price = data.compare_at_price;
  //   }
  //   if (data.original_price > data.price) {
  //     price = data.original_price;
  //   }

  //   let product_options = "";

  //   product_options += `
	// 	  <div class="item-quantity">
	// 		<span>Quantity:</span>
	// 		<span>${data.quantity}</span>
	// 	  </div>
	// 	`;

  //   data.options_with_values.forEach((item) => {
  //     product_options += ` 
	// 	  <div class="item-option">
	// 		<span>${item.name}:</span>
	// 		<span>${item.value}</span>
	// 	  </div>`;
  //   });

  //   $(".cart-popup-item__title").html(data.product_title);
  //   $(".cart-popup-item__image-wrapper").html(`<img src="${data.image}" />`);
  //   $(".cart-popup-item__price").html(Shopify.formatMoney(price));
  //   $(".cart-popup-item__options").html(product_options);

  //   $(".cart-popup-wrapper").show();

  //   setTimeout(() => {
  //     $(".cart-popup-wrapper").hide();
  //   }, 3000);
  // }

  function showPortal($productCard) {
    var $optionsWrapperPortal = $("#options-wrapper-portal");
    $optionsWrapperPortal
      .find(".portal-close")
      .off("click")
      .on("click", function () {
        hidePortal();
      });
    var $optionWrapper = $productCard.find(
      ".option-wrapper[data-option-index=1]"
    );
    var $optionWrapperClone = $optionWrapper.clone();
    $optionsWrapperPortal.addClass("active");
    $optionsWrapperPortal
      .find(".content-wrapper")
      .empty()
      .append($optionWrapperClone);
  }

  function hidePortal($productCard) {
    var $optionsWrapperPortal = $("#options-wrapper-portal");
    $optionsWrapperPortal.removeClass("active");
    $optionsWrapperPortal.find(".content-wrapper").empty();
  }

  function onClassChange($element, callback) {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          callback($element);
        }
      }
    });

    observer.observe($element[0], {
      attributes: true,
      attributeFilter: ["class"],
    });
    return observer; // so you can disconnect it later if needed
  }

  onClassChange($("body"), function (el) {
    if (el.hasClass("flyout__main_menu--open")) {
      hidePortal();
    }
  });

  var atcProductCards = $(".product-card-cta-enabled");
  atcProductCards.each(function () {
    var $productCard = $(this);
    var $productCardOptionsWrapper = $productCard.find(
      ".product-options-wrapper"
    );
    var $productCardId = $(this).attr("id");
    var productsDataJson = $productCard.find("[data-json]");
    if (productsDataJson.length == 0) return;
    var productsData = JSON.parse(productsDataJson.text());
    if (!productsData || productsData.length == 0) return;

    $productCard.find(".btn-mobile-portal").on("click", function () {
      $me = $(this);
      showPortal($productCard);
    });

    var variantsData = productsData
      .map((productData) =>
        productData.variants.map((v) => ({ ...v, productId: productData.id }))
      )
      .reduce((arr, items) => [...arr, ...items], []);

    var optionsWithValues = [];
    productsData[0].options.forEach((option, optionIndex) => {
      var variantOptions = [
        ...new Set(
          variantsData.map((variantData) => variantData.options[optionIndex])
        ),
      ];
      optionsWithValues = [
        ...optionsWithValues,
        {
          optionIndex,
          option,
          values: variantOptions,
        },
      ];
    });

    // create group handlers
    var $groupHandlerMain = $("<div></div>").addClass("group-handlers");
    productsData.forEach((productData) => {
      var $radio = $(
        `<input type='radio' name='group-handler-${$productCardId}' id="group-handler-${$productCardId}-${productData.handle}">`
      )
        .css({ width: "1em", height: "1em" })
        .addClass("radio-group-handler")
        .val(productData.handle);

      var hasColorIndex = productData.options.findIndex(
        (option) =>
          option.toLowerCase() === "color" || option.toLowerCase() === "colour"
      );
      if (hasColorIndex > -1) {
        $radio.attr(
          "data-color",
          productData.variants[0].options[hasColorIndex]
        );
      }

      var $label = $("<label></label>")
        .attr("for", `group-handler-${$productCardId}-${productData.handle}`)
        .text(productData.handle);
      var $groupHandler = $("<div></div>")
        .css({
          display: "flex",
        })
        .append($radio)
        .append($label);
      $groupHandlerMain.append($groupHandler);
    });
    $productCardOptionsWrapper.append($groupHandlerMain);
    // group handlers listeners
    $productCard.find(".radio-group-handler").on("change", function () {
      if (this.checked) {
        // making sure it doesn't find a variant when switching colors
        $productCard
          .find(`.option-wrapper[data-option-index="1"] .option-value:checked`)
          .prop("checked", false);

        $productCard
          .find(`.option-value[value='${$(this).data("color")}']`)
          .prop("checked", true)
          .trigger("change");
      }
    });

    // create form
    var $productForm = $("<div></div>");
    optionsWithValues.forEach((optionWithValue) => {
      var $optionWithValueForm = $("<div></div>")
        .addClass("option-wrapper")
        .attr("data-option", optionWithValue.option)
        .attr("data-option-index", optionWithValue.optionIndex);

      if (
        optionWithValue.option.toLowerCase() == "color" ||
        optionWithValue.option.toLowerCase() == "colour"
      ) {
        $optionWithValueForm.css("display", "none");
      }
      optionWithValue.values.forEach((value) => {
        var $radio = $(
          `<input type='radio' name='option-${optionWithValue.optionIndex}-${$productCardId}' id="option-${optionWithValue.optionIndex}-${value}-${$productCardId}">`
        )
          .css({ width: "1em", height: "1em" })
          .addClass("option-value")
          .val(value);
        var $label = $("<label></label>")
          .attr(
            "for",
            `option-${optionWithValue.optionIndex}-${value}-${$productCardId}`
          )
          .text(value);
        var $valueOptionWrapper = $("<div></div>")
          .addClass("value-wrapper")
          .append($radio)
          .append($label);
        $optionWithValueForm.append($valueOptionWrapper);
      });
      $productForm.append($optionWithValueForm);
    });
    $productCardOptionsWrapper.append($productForm);

    // Seleccionar la primera variante disponible por defecto
    var defaultVariant = variantsData.find(variant => variant.available) || variantsData[0];
    if (defaultVariant) {
      defaultVariant.options.forEach((optionValue, optionIndex) => {
        var $optionRadio = $productCard.find(`[data-option-index="${optionIndex}"] .option-value[value="${optionValue}"]`);
        if ($optionRadio.length) {
          $optionRadio.prop("checked", true);
        }
      });
    }

    // radio button listeners
    $productCard.find(".option-value").on("change", function () {
      var optionValue = $(this).val();
      if ($(this).is(":checked")) {
        var optionValue = $(this).val();
        var optionIndex = $(this)
          .closest(".option-wrapper")
          .data("optionIndex");

        // Validating Colors
        if (optionIndex == 0) {
          hidePortal($productCard);
          var allVariants = variantsData.filter(
            (variant) => variant.options[0] === optionValue
          );
          
          // Deshabilitar todos los tamaños primero
          $productCard.find(`[data-option-index="1"] .option-value`).prop("disabled", true);
          
          // Habilitar solo los tamaños disponibles para este color
          allVariants.forEach((variant) => {
            $productCard
              .find(
                `[data-option-index="1"] .option-value[value="${variant.options[1]}"]`
              )
              .prop("disabled", !variant.available);
          });
          
          // Seleccionar automáticamente el primer tamaño disponible para este color
          var firstAvailableVariant = allVariants.find(variant => variant.available);
          if (firstAvailableVariant) {
            var $firstSizeOption = $productCard.find(`[data-option-index="1"] .option-value[value="${firstAvailableVariant.options[1]}"][disabled="false"]`);
            if ($firstSizeOption.length) {
              $firstSizeOption.prop("checked", true).trigger("change");
            }
          }
        }
        
        // Si se selecciona un tamaño y no hay color seleccionado, seleccionar el primer color disponible
        if (optionIndex == 1) {
          var $selectedColor = $productCard.find(`[data-option-index="0"] .option-value:checked`);
          if (!$selectedColor.length) {
            // Buscar el primer color que tenga este tamaño disponible
            var variantsWithThisSize = variantsData.filter(
              (variant) => variant.options[1] === optionValue && variant.available
            );
            if (variantsWithThisSize.length > 0) {
              var firstColor = variantsWithThisSize[0].options[0];
              var $firstColorOption = $productCard.find(`[data-option-index="0"] .option-value[value="${firstColor}"]`);
              if ($firstColorOption.length) {
                $firstColorOption.prop("checked", true).trigger("change");
                return; // Salir aquí para evitar procesamiento duplicado
              }
            }
          }
        }

        // find variant
        var selectedValues = [];
        $productCard.find(".option-value:checked").each(function () {
          var $optionSelected = $(this);
          selectedValues[
            parseInt(
              $optionSelected.closest(".option-wrapper").data("optionIndex")
            )
          ] = $optionSelected.val();
        });

        var selectedVariant = variantsData.find(
          (variant) =>
            variant.options.length === selectedValues.length &&
            variant.options.every((v, i) => v === selectedValues[i])
        );

        if (selectedVariant) {
          var dataItem = { id: selectedVariant.id, quantity: 1 };

          var selectedProduct = productsData.find(
            (product) => product.id === selectedVariant.productId
          );
          var dataProduct = {
            price: selectedVariant.price,
            compare_at_price: selectedVariant.compare_at_price,
            original_price: selectedVariant.compare_at_price,
            quantity: 1,
            product_title: selectedProduct.title,
            image: selectedProduct.featured_image,
            options_with_values: selectedProduct.options.map(
              (option, optionIndex) => ({
                name: option,
                value: selectedVariant.options[optionIndex],
              })
            ),
          };
          $productCard
            .find(".value-wrapper > label")
            .css({ pointerEvents: "none" });
          theme.cart.add(dataItem).done(function () {
            // Update sidecart and cart count
            if (window.SideCart) {
              window.SideCart.updateSideCart(true);
              // Also update cart count directly to ensure it updates
              window.SideCart.updateCartCount();
            }
            
            // Dispatch cart:added event to ensure all listeners are notified
            document.dispatchEvent(new CustomEvent('cart:added', {
              detail: { cart: theme.cart.content }
            }));
            
            $productCard
              .find(".value-wrapper > label")
              .css({ pointerEvents: "all" });
          });
        }
      }
    });
  });
})(jQuery);

if (window.innerWidth < 799) {
  if (document.body.classList.contains("template-product")) {
    let target = document.querySelector("[data-hide-sticky]");
    const observer = new IntersectionObserver(handleIntersection, {
      threshhold: 0.01,
    });
    observer.observe(target);

    function handleIntersection(entries, observer) {
      entries.forEach((entry) => {
        let stickyContainer = document.querySelector("#stickyATC");
        if (entry.isIntersecting) {
          stickyContainer.classList.add("hidden");
        } else {
          stickyContainer.classList.remove("hidden");
        }
      });
    }
  }
}

if (document.body.classList.contains("template-collection")) {
  document.addEventListener("DOMContentLoaded", function () {
    setTimeout(() => {
      $("#collection_body").add(".loading-overlay").removeClass("loading");
    }, 1500);
  });

  if (window.innerWidth < 1025) {
    $("body").on("click", ".btn-filters-close", function () {
      $('[data-filter-control="filter"]').trigger("click");
      $("body").removeClass("no-scroll");
    });

    $("body").click(function (e) {
      if (e.target.closest(".overlay")) {
        $('[data-filter-control="filter"]').trigger("click");
        $("body").removeClass("no-scroll");
      }
    });

    let lastScrollTop = 0;
    $(window).scroll(function () {
      let documentPositionFromTop = document.documentElement.scrollTop;
      if (navigator.userAgent.match(/(iPod|iPhone|iPad|Android)/)) {
        documentPositionFromTop = window.pageYOffset;
      }

      if (
        documentPositionFromTop > lastScrollTop &&
        documentPositionFromTop < $(".collection").height()
      ) {
        $("body").removeClass("scroll-up").addClass("scroll-down");
      } else {
        if (documentPositionFromTop < $(document).height()) {
          $("body").removeClass("scroll-down").addClass("scroll-up");
        }
        if (documentPositionFromTop == 0) {
          $("body").removeClass("scroll-down").removeClass("scroll-up");
        }
      }

      lastScrollTop =
        documentPositionFromTop <= 0 ? 0 : documentPositionFromTop;
    });
  }
  $("body").on("click", ".trigger-toggle-filters", function () {
    $('[data-filter-control="filter"]').trigger("click");
    if ($('[data-filter-control="filter"]').hasClass("filters-ui--active")) {
      $(this).find("span").html("Hide filter");
    } else {
      $(this).find("span").html("Show filter");
    }
  });

  $("body").on("click", ".c-collection__filters__wrapper h4", function () {
    $(this).toggleClass("is-open");
  });
}
